/**
 * Orientation Editor — single-page authoring tool for orientations.
 *
 * Build an orientation by placing stops on a 2D satellite map and authoring each
 * stop's beat timeline (Command / Narrate / Wait actions). Ported from
 * the approved in-environment mockup
 * (`Plans/02-approved/MINETWIN-UNREAL-ORIENTATION-AUTHORING-mockup/index.html`),
 * with the 3D first-person scene replaced by a Leaflet/satellite map.
 *
 * All orientation data (stops incl. geo-location, scripts, actions) is owned by
 * the minetwin module — nothing is read from `core` for placement.
 *
 * Save model (auto-apply, 2026-08-28):
 *   - Structural edits commit LIVE (single-row reducers): create orientation,
 *     update details, publish/archive, add/remove/reorder step.
 *   - The selected stop's fields + beat timeline are edited against a
 *     local DRAFT that auto-commits on a short debounce (no Save button) via
 *     update_orientation_step + set_orientation_step_script +
 *     set_script_actions (the latter a bulk replace). Ctrl+S force-flushes;
 *     switching stops flushes the pending edit instead of prompting.
 *
 * Known v1 limitations: an associated twin can be set/changed but not cleared to
 * none (update_orientation_step treats None as "leave unchanged"); "Remove"
 * unlinks the step from the orientation (the step entity is not deleted).
 */
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { useEnsureConnection } from '@/services/connectionManager/useEnsureConnection';
import { type DbConnection as MinetwinConn, tables as minetwinTables } from '@sdk/minetwin';
import { tables as coreTables } from '@sdk/core';
import { tables as assetTables } from '@sdk/asset';
import { tables as telemetryTables } from '@sdk/telemetry';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { useProjects } from '@/hooks/useProjects';
import { useUsers } from '@/hooks/useUsers';
import { useTweaks } from '@/hooks/useTweaks';
import { useUnits } from '@/hooks/useUnits';
import type { Site, Toast } from '@/data/types';
import { useEditorModel } from './useEditorModel';
import {
  AUTHORED_SOURCE, CMD_FIELDS, EDIT_VERSION, STATUS_DESC, STATUS_TAGS, authoredBeats, beatDriver, beatsToEntries,
  addWorldSelectionTwin, beatsError, cameraTargetIssue, checksError, checksForBeat, checksToEntries, cloneBeats, cloneChecks, commandGeoMarkers, commandLocation, commandPoints, commandTargetTwin, estimateDwellMinutes, estimateMs, flightPoints, flyLookTargets, isContinuousCam, isPathCommand, isWorldSelection, laneOf, laneTaken, makeCheck, withCommandPoints,
  makeCommand, narrMs, pickerOrientations, playHoldMs, uid,
  type Beat, type CommandAction, type EditorAction, type EditorCheck, type EditorModel, type NarrateAction,
  type EditorOrientation, type EditorStep, type LaneKey, type VersionTag,
  scriptWrite,
} from './model';
import { QuizPanel, QuizResults, type QuizOutcome } from './QuizPanel';
import { runCreateOrientation, runSaveDetails, splitOcc } from './orientationSave';
import { elevationAt } from './terrainElevation';
import { DEFAULT_VERSION } from '@/lib/orientationVersion';
import { StopsPanel } from './StopsPanel';
import { StepMap, LayersMenu, type StepMapStop, type TwinMarker, type MoverMarker, type GeoGroup, type BasemapKey } from './StepMap';
import { extractGeoObjects } from 'turbotwin-viewer-react/map';
import { ViewerPane, type FlyToGeoArgs, type FlyToLookAtArgs, type MeasureChanged, type SetOverlayArgs, type ViewerPaneHandle } from './ViewerPane';
import { MeasureTool } from './MeasureTool';
import type { CommandLocation, CommandTarget } from '@/generated/minetwin/types';
import { Inspector, type PickTarget } from './Inspector';
import { Timeline, type ActionSel } from './Timeline';
import { useEditorLayout } from './useEditorLayout';
import { isNoopMove, moveItem } from './layout';
import { CameraControl } from './CameraControl';
import {
  encodeParams,
  loadCameraValues,
  saveCameraValues,
  type CameraModeValues,
} from './cameraModes';
import { OrientationDetails, type OrientationDraft } from './OrientationDetails';
import { StepPicker, type PickerCandidate } from './StepPicker';
import { RemoveStopDialog } from './RemoveStopDialog';
import { SearchSelect } from './SearchSelect';
import { GatedButton } from '../GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';
import './orientation-editor.css';

// Standing eye height (metres) the camera rides above a ground point — used both
// for FlyAlongPath waypoints and the stop camera so they share one source of
// truth. Kept ~1.5 m so the view stays inside a tight tunnel (~2 m headroom)
// instead of clipping through the ceiling.
const CAM_EYE_HEIGHT_M = 1.5;

type OrientationEditorProps = { toast: (t: Omit<Toast, 'id'>) => void };

// Clones EVERY authored slot, not just the edited one: a legacy timed script
// rides along untouched so nothing looks lost while it is still in its old slot.
function cloneStep(s: EditorStep): EditorStep {
  const scripts: Partial<Record<VersionTag, Beat[]>> = {};
  for (const [v, beats] of Object.entries(s.scripts) as [VersionTag, Beat[]][]) {
    scripts[v] = cloneBeats(beats);
  }
  return { ...s, scripts, checks: cloneChecks(s.checks) };
}

/** Versions other than {@link EDIT_VERSION} that still hold an authored script
 *  (legacy timed slots, pending the Phase 4 data migration). Display only. */
function legacySlotsWithScript(s: EditorStep): VersionTag[] {
  return (Object.entries(s.scripts) as [VersionTag, Beat[]][])
    .filter(([v, beats]) => v !== EDIT_VERSION && beats.length > 0)
    .map(([v]) => v);
}

/** Last orientation + stop the author had open, per site. Browser-local only,
 *  so a refresh or a tab round-trip lands back on the same stop. */
type Selection = { orientationId: string; stepId: string | null };
/** What the toolbar's Preview play button runs. */
type PreviewScope = 'orientation' | 'stop';
function loadSelection(projectId: string): Selection | null {
  try { return JSON.parse(localStorage.getItem(`oe.selection.${projectId}`) ?? 'null'); } catch { return null; }
}
function saveSelection(projectId: string, sel: Selection): void {
  try { localStorage.setItem(`oe.selection.${projectId}`, JSON.stringify(sel)); } catch { /* best-effort */ }
}

export function OrientationEditor({ toast }: OrientationEditorProps) {
  const dialogs = useDialogs();
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const { units, fmtElevation } = useUnits();
  const minetwinState = useSpacetimeDB('minetwin');
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const projectId = currentSite?.projectName ?? '';
  const membership = useMySiteMembership(projectId);
  // No valid site selected (e.g. the org has no projects, or the org
  // subscription is still loading): there is nothing to create an
  // orientation into, so the create entry points must be disabled.
  const hasSite = projectId !== '';

  const { model, orientationsReady: modelReady, actionsReady, checksReady } = useEditorModel();
  // #243: an empty list while the minetwin tables are not ready (first load
  // or mid-reconnect) is "still loading", not "no orientations".
  const minetwinOnline = minetwinState.isActive;

  const projectOrientations = useMemo(
    () => model.orientations
      .filter((o) => o.projectId === projectId)
      // Picker order = STATUS_TAGS order: Active first, then Draft, Archived
      // last, so the default selection lands on a published orientation.
      .sort((a, b) =>
        STATUS_TAGS.indexOf(a.status) - STATUS_TAGS.indexOf(b.status) ||
        a.displayName.localeCompare(b.displayName)),
    [model.orientations, projectId],
  );

  const [currentId, setCurrentId] = useState<string>(() => loadSelection(projectId)?.orientationId ?? '');
  // Sites resolve after mount, so re-read the remembered orientation once the
  // site is known (and drop a stale one when the site changes).
  useEffect(() => { setCurrentId(loadSelection(projectId)?.orientationId ?? ''); }, [projectId]);
  // A remembered id that no longer exists (deleted elsewhere) falls back to
  // the first orientation instead of an empty editor.
  const orientationId = (projectOrientations.some((o) => o.orientationId === currentId) ? currentId : projectOrientations[0]?.orientationId) || '';
  const currentOrientation = projectOrientations.find((o) => o.orientationId === orientationId) ?? null;

  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = projectOrientations.filter((o) => o.status === 'Archived').length;

  // Author filter: '' = everyone, else a user_id (the "Mine" option is just my
  // own id). Names come from the roster when the caller can see it; a Writer
  // without roster scope sees the raw id instead.
  const [authorFilter, setAuthorFilter] = useState('');
  const [myUserRows] = useTable('minetwin', minetwinTables.my_user);
  const myUserId = myUserRows[0]?.userId ?? '';
  const { users } = useUsers();
  const authorName = (id: string) => users.find((u) => u.userId === id)?.displayName ?? id;
  const authors = useMemo(
    () => [...new Set(projectOrientations.map((o) => o.createdBy).filter((id) => id && id !== myUserId))]
      .sort((a, b) => authorName(a).localeCompare(authorName(b))),
    [projectOrientations, myUserId, users],
  );
  const listedOrientations = pickerOrientations(projectOrientations, showArchived, orientationId, authorFilter);

  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selAction, setSelAction] = useState<ActionSel | null>(null);
  // Index into the selected stop's checks. One inspector, one selection:
  // picking a check drops the action selection and vice versa.
  const [selCheck, setSelCheck] = useState<number | null>(null);
  const [modal, setModal] = useState<null | 'new' | 'details' | 'pick' | 'remove'>(null);
  // Placement mode: armed by "+ Add stop", the next map click creates the stop.
  const [placingStop, setPlacingStop] = useState(false);
  // Pick-in-scene: armed by the pick button on a Location row (the selected
  // stop's, a geo command's, or a Fly + look Location target's), the next
  // 2D/3D click fills that location. Mutually exclusive with placingStop
  // (one armed click target at a time).
  const [pickTarget, setPickTarget] = useState<PickTarget | null>(null);
  // Which waypoint a 'pathNode' pick/camera-place fills (multi-location FlyTo).
  const [activePathIndex, setActivePathIndex] = useState(0);
  // Map pane: the embedded 3D platform viewer (default) vs 2D Leaflet. The
  // viewer is WebGPU-only, so browsers without it fall back to 2D.
  const [viewMode, setViewMode] = useState<'2d' | '3d'>(() => ('gpu' in navigator ? '3d' : '2d'));
  // 3D camera mode (orientation editor's 3-mode camera, ported from MineZero):
  // Free Orbit (default), Free Look (drone), and Walk (grounded first-person).
  const [cameraMode, setCameraMode] = useState<'freeLook' | 'freeOrbit' | 'walk'>('freeOrbit');
  // Key of the command whose continuous preview (Orbit / Follow) is running in
  // the 3D viewer, or null. Flips that command's Preview button to "Stop
  // Preview"; the viewer keeps the motion going until we send `stopCamera`.
  const [previewingKey, setPreviewingKey] = useState<string | null>(null);
  // A narration "Preview in 3D" shows its caption over the viewer (no play run);
  // this flag lets the caption overlay render outside a full play.
  const [captionPreviewing, setCaptionPreviewing] = useState(false);
  // Auto-stop timer for a duration-bounded continuous preview (an Orbit/Follow
  // with an authored duration); stays null when the preview is open-ended and
  // runs until the author hits Stop.
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hides the relationship lines a ShowRelationships preview turned on (the exact
  // twins / global toggle it used), so Stop, navigate-away, or its own duration
  // timer can clear them — the viewer has no "clear all relationships" verb.
  const relPreviewHideRef = useRef<(() => void) | null>(null);
  // Whole-orientation playback (PLAY-ORIENTATION-IN-WEB, see the play loop far
  // below): whether a run is live, the caption card contents, and the beat the
  // flight is currently on (drives the Timeline's "now playing" highlight). The
  // cancellation channel lets Stop cut a beat's hold short instantly.
  const [playing, setPlaying] = useState(false);
  const [playCaption, setPlayCaption] = useState<{ stop: string; text: string; progress: string } | null>(null);
  const [playPos, setPlayPos] = useState<{ stepId: string; bi: number } | null>(null);
  // Play-speed multiplier for the WHOLE orientation (the Play-button dropdown).
  // Scales the per-beat hold AND the viewer's camera clock (setPlaybackSpeed), so
  // beat pacing and camera motion speed up / slow down together. A ref mirrors it
  // so the running play loop's holds pick up a mid-play change without a restart.
  const [playSpeed, setPlaySpeed] = useState(1);
  const playSpeedRef = useRef(1);
  // What the Preview group's play button runs: the whole orientation or only
  // the selected stop (sticky across runs; see the toolbar).
  const [previewScope, setPreviewScope] = useState<PreviewScope>('orientation');
  // The comprehension check the Play preview is presenting right now (graded in
  // the browser, no DB writes), and the end-of-run results once playback ends.
  const [activeCheck, setActiveCheck] = useState<EditorCheck | null>(null);
  const [quizResults, setQuizResults] = useState<QuizOutcome[] | null>(null);
  // Interactive stop gate (Unreal-style): while playing, each stop shows its tube
  // marker and holds until the author flies into it (`reached`) and confirms
  // Start/Advance. `stopConfirmRef` resolves the loop's await when they confirm.
  const [stopGate, setStopGate] = useState<{ stepId: string; label: 'Start' | 'Advance'; reached: boolean } | null>(null);
  const stopConfirmRef = useRef<(() => void) | null>(null);
  // The Effects-lane highlight during playback is driven IMPERATIVELY through the
  // viewer's `highlightTwin` verb (see the play loop), so the pulsing marker
  // lands on the twin's ACTUAL position, not its stored geo anchor. The
  // declarative `highlightGeo` prop is left unmanaged (`undefined`) while a run
  // is live so the two don't fight over the same marker.
  // Suppress the stop-center focus fly during AND after playback: playback moves
  // the camera via beat verbs, and when it ends the camera must stay on the last
  // twin, not snap to the last stop's centre. Cleared when the author next picks a
  // stop or action (a deliberate navigation re-enables focus).
  const [focusSuppressed, setFocusSuppressed] = useState(false);
  const playRef = useRef<{
    cancelled: boolean;
    timer: ReturnType<typeof setTimeout> | null;
    wake: (() => void) | null;
    // Pending auto-revert of a Transparency effect (its authored duration). Held
    // here so Stop/end/unmount can cancel it and the fade never outlives playback.
    fadeTimer: ReturnType<typeof setTimeout> | null;
    // Pending auto-clear of a Highlight effect (its authored duration — 5 s by
    // default, matching the runtime). Same lifetime rules as fadeTimer.
    hlTimer: ReturnType<typeof setTimeout> | null;
    // Pending auto-hide of a ShowRelationships effect (its authored duration).
    // Same lifetime rules as fadeTimer/hlTimer; null when the lines persist (no
    // authored duration) or none are shown.
    relTimer: ReturnType<typeof setTimeout> | null;
    // Pending auto-hide of a ShowTwinInfo panel (its authored duration). Same
    // lifetime rules as fadeTimer/relTimer; null when none is shown.
    twinInfoTimer: ReturnType<typeof setTimeout> | null;
    // Resolver for the check the play loop is currently gated on (present ->
    // answer). Stop/unmount calls it so the awaiting loop unblocks and then bails.
    checkResolve: ((correct: boolean) => void) | null;
    // Re-time the IN-FLIGHT beat hold after a live play-speed change: subtract the
    // beat-time already consumed and reschedule the remainder at the new speed, so
    // changing the dropdown mid-beat takes effect on the current beat, not the next.
    reschedule: (() => void) | null;
    // Re-pace the IN-FLIGHT camera move after a live play-speed change: re-fire the
    // current fly/look verb with the remaining time rescaled (set by fireBeatCamera's
    // armCam). null between camera moves / for path & continuous verbs.
    recam: (() => void) | null;
  }>(
    { cancelled: false, timer: null, wake: null, fadeTimer: null, hlTimer: null, relTimer: null, twinInfoTimer: null, checkResolve: null, reschedule: null, recam: null },
  );
  // Per-mode camera tuning (persisted per-author in localStorage). Encoded into
  // the integer wire bag for the active mode and sent to the viewer.
  const [cameraValues, setCameraValues] = useState<CameraModeValues>(() => loadCameraValues());
  const onCameraValues = (v: CameraModeValues) => {
    setCameraValues(v);
    saveCameraValues(v);
  };
  const cameraParams = useMemo(
    () => encodeParams(cameraMode, cameraValues),
    [cameraMode, cameraValues],
  );
  // Placement works in BOTH modes now: a 2D map click gives lat/lon and samples
  // the viewer's terrarium DEM for the elevation (see `placeStopFrom2D`), a 3D
  // pick-point click gives lat/lon AND a raycast elevation. The
  // placement hint renders in either pane, so the arm can persist across a
  // 2D/3D toggle. Free-fly (hold-RMB) is armed for the whole 3D session by the
  // ViewerPane `flyMode` prop, so there's no per-mode state to reset here.
  const changeViewMode = (m: '2d' | '3d') => setViewMode(m);
  // Twin markers on/off, one flag for BOTH panes: gates the 2D cluster layer
  // and drives the viewer's setTagMarkersVisible.
  const [showTwinMarkers, setShowTwinMarkers] = useState(true);
  // 2D basemap, owned here so its picker can live in the shared top bar.
  const [basemap, setBasemap] = useState<BasemapKey>('satellite');

  // Local working copy of the selected step (fields + timelines).
  const [draft, setDraft] = useState<EditorStep | null>(null);
  const [dirty, setDirty] = useState(false);

  // Floating workspace: the map fills the editor; Stops / Inspector are
  // draggable, resizable cards over it; the timeline is a bottom drawer.
  // Geometry persists per browser.
  const { canvasRef, dockStyle, floatShell, drawerShell, expandPanel, expandTimeline, resetLayout } = useEditorLayout();

  const orderedStepIds = useMemo(
    () => model.links.get(orientationId) ?? [],
    [model.links, orientationId],
  );

  // Steps in this project that are NOT already in the current orientation:
  // the candidate pool for "Add existing" (includes orphans, usage 0).
  const pickerCandidates = useMemo<PickerCandidate[]>(() => {
    const inOrientation = new Set(orderedStepIds);
    return [...model.steps.values()]
      .filter((s) => s.projectId === projectId && !inOrientation.has(s.stepId))
      .map((s) => ({
        stepId: s.stepId,
        name: s.name,
        usage: model.usage.get(s.stepId) ?? 0,
        beats: (s.scripts[EDIT_VERSION] ?? []).length,
      }));
  }, [model.steps, model.usage, projectId, orderedStepIds]);

  // How many orientations use the selected stop (drives remove/save behaviour).
  const selectedUsage = selectedStepId ? model.usage.get(selectedStepId) ?? 0 : 0;

  // Keep a valid selected stop as the orientation loads / its step list
  // changes. The functional updater returns the SAME id while it's still
  // present, so a subscription tick that re-derives an identical list is a
  // no-op (React bails out) — the author's stop stays put instead of snapping
  // back to the first stop.
  useEffect(() => {
    setSelectedStepId((prev) => {
      if (prev && orderedStepIds.includes(prev)) return prev;
      const saved = loadSelection(projectId);
      const remembered = saved?.orientationId === orientationId ? saved.stepId : null;
      return remembered && orderedStepIds.includes(remembered) ? remembered : orderedStepIds[0] ?? null;
    });
  }, [projectId, orientationId, orderedStepIds]);

  // Remember the open orientation + stop for the next refresh / tab visit.
  // While steps are still loading the stop is null; keep the remembered one
  // for the same orientation so the effect above can still restore it.
  useEffect(() => {
    if (!projectId || !orientationId) return;
    const prev = loadSelection(projectId);
    const stepId = selectedStepId ?? (prev?.orientationId === orientationId ? prev.stepId : null);
    saveSelection(projectId, { orientationId, stepId });
  }, [projectId, orientationId, selectedStepId]);

  // Clear the action selection only when the step context it indexes
  // into actually changes. This must NOT hang off `orderedStepIds`: that array
  // gets a fresh reference on every subscription tick (the shared connection
  // object re-derives all tables, even with identical content), so depending on
  // it here silently wiped the author's selected command mid-edit whenever any
  // background table update ticked the model — the "selection keeps resetting"
  // bug. `orientationId` / `selectedStepId` only change on real navigation,
  // never on a tick.
  useEffect(() => {
    setSelAction(null);
    setSelCheck(null);
  }, [orientationId, selectedStepId]);

  // Cancel placement mode when switching orientations.
  useEffect(() => { setPlacingStop(false); }, [orientationId]);

  // Escape cancels placement mode.
  useEffect(() => {
    if (!placingStop) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlacingStop(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placingStop]);

  // The stop whose draft checks came from a LOADED check cache. The two check
  // views are their own subscription batch, so they can land after the step
  // rows (or never, on an org whose checks republish has not landed): a draft
  // seeded before that holds an empty check list, and set_step_checks is a bulk
  // replace, so saving it would delete the step's authored checks. saveStop
  // writes checks only for the stop named here.
  const checksSeededRef = useRef<string | null>(null);

  // Seed the draft once per step selection (and when a freshly-created step
  // arrives from the subscription). We intentionally do NOT re-seed on every
  // model tick: it would churn the map and, right after a save, briefly revert
  // the inspector to the pre-save projection before the subscription catches up.
  // Trade-off: a concurrent external edit to the open step won't live-refresh,
  // and switching stops discards unsaved edits silently (single-author v1).
  useEffect(() => {
    if (!selectedStepId) { setDraft(null); setDirty(false); checksSeededRef.current = null; return; }
    const s = model.steps.get(selectedStepId);
    if (!s) return; // not arrived yet; a later model tick will seed it
    if (draft?.stepId === selectedStepId) {
      // Same stop, but the checks have arrived since it was seeded: adopt them,
      // so the next save replaces them with themselves rather than with
      // nothing. This runs once per stop, inside the load window, and a check
      // added in that window is dropped with it: it could not have been saved
      // either way, since the write below waits for the same signal.
      if (checksReady && checksSeededRef.current !== selectedStepId) {
        checksSeededRef.current = selectedStepId;
        setDraft((d) => (d ? { ...d, checks: cloneChecks(s.checks) } : d));
      }
      return;
    }
    checksSeededRef.current = checksReady ? selectedStepId : null;
    setDraft(cloneStep(s));
    setDirty(false);
  }, [selectedStepId, model, draft?.stepId, checksReady]);

  // Twin targets for camera/highlight commands: the selected project's LIVE
  // core twins, nothing else (`digital_twin` is already in the core
  // subscription). Ids referenced by the doc but absent from core (stale
  // authoring, e.g. the pre-fix hardcoded 'MineZero' default) are NOT merged
  // in — the Inspector prepends an action's own saved value to its dropdown,
  // labeled as not-in-project, so stale references stay visible exactly where
  // they need fixing without polluting the general list.
  const [coreTwins] = useTable('core', coreTables.digitalTwin);

  // Per-twin display names (TWIN-DISPLAY-NAME chain, same rule as every
  // client): the property named by system_config "display_name_property",
  // then "displayName", then "name" — via twin_property →
  // primitive_property_string, all subscribed. Feeds the Target-twin dropdown
  // labels, map tooltips, and timeline captions; ids stay searchable via the
  // dropdown value.
  const [twinPropRows] = useTable('core', coreTables.twinProperty);
  // twinId -> its telemetry stream names (twin_telemetry_link), for ShowTwinInfo.
  const [twinTelemetryRows] = useTable('core', coreTables.twinTelemetryLink);
  const [stringPropRows] = useTable('core', coreTables.primitivePropertyString);
  const [systemConfigRows] = useTable('core', coreTables.systemConfig);
  const twinNames = useMemo(() => {
    const candidates: string[] = [];
    const configured = systemConfigRows
      .find((r) => r.configKey === 'display_name_property')
      ?.configValue.trim();
    if (configured) candidates.push(configured);
    for (const fallback of ['displayName', 'name']) {
      if (!candidates.includes(fallback)) candidates.push(fallback);
    }
    const valById = new Map(stringPropRows.map((p) => [String(p.propertyId), p.value]));
    const m = new Map<string, string>();
    const rankByTwin = new Map<string, number>();
    for (const tp of twinPropRows) {
      const rank = candidates.indexOf(tp.name);
      if (rank === -1) continue;
      const v = valById.get(String(tp.propertyId));
      if (!v) continue;
      const current = rankByTwin.get(tp.twinId);
      if (current !== undefined && current <= rank) continue;
      rankByTwin.set(tp.twinId, rank);
      m.set(tp.twinId, v);
    }
    return m;
  }, [twinPropRows, stringPropRows, systemConfigRows]);

  // Sorted by display label (id fallback) so the dropdown reads naturally.
  const twinOptions = useMemo(
    () =>
      coreTwins
        .map((t) => t.twinId)
        .sort((a, b) => (twinNames.get(a) ?? a).localeCompare(twinNames.get(b) ?? b)),
    [coreTwins, twinNames],
  );

  // Geo-located twins for the 2D map, mirroring the twin markers the 3D
  // viewer already shows — the main spatial reference when placing stops.
  const [twinMeta] = useTable('core', coreTables.turbotwinMetadata);

  // Live per-twin pose stream (telemetry module `twin_transform`): the same
  // table the webclient map and the 3D viewer animate moving twins from. Feeds
  // the 2D map's truck markers so they track their real position + heading.
  const [transformRows] = useTable('telemetry', telemetryTables.twinTransform);

  // twinId → telemetry stream name. Primary source is turbotwin_metadata
  // (Dynamic twins), with a legacy fallback to twin_telemetry_link's
  // telemetryName='location' row (pre-stream_name data) — same resolution the
  // webclient map uses.
  const streamByTwin = useMemo(() => {
    const m = new Map<string, string>();
    for (const meta of twinMeta) {
      if (meta.locationMode.tag === 'Dynamic' && meta.streamName) m.set(meta.twinId, meta.streamName);
    }
    for (const link of twinTelemetryRows) {
      if (link.telemetryName === 'location' && !m.has(link.twinId)) m.set(link.twinId, link.streamName);
    }
    return m;
  }, [twinMeta, twinTelemetryRows]);

  // streamName → latest position + compass heading. twin_transform.yaw is
  // ALREADY a compass bearing in degrees (0 = N, 90 = E, clockwise) — the
  // platform telemetry convention (`send_transform_by_stream_name` yaw=compass;
  // the simulator sends `this.heading` in degrees), which is exactly a top-down
  // icon's rotation. So just normalise it; no radian conversion or sign flip
  // (the earlier `-yaw·180/π` wrongly treated this degree value as a bevy
  // internal-transform yaw in radians, scrambling the marker heading).
  const posByStream = useMemo(() => {
    const m = new Map<string, { lat: number; lon: number; heading: number }>();
    for (const row of transformRows) {
      if (!row.streamName) continue;
      const heading = ((row.yaw % 360) + 360) % 360;
      m.set(row.streamName, { lat: row.latitude, lon: row.longitude, heading });
    }
    return m;
  }, [transformRows]);

  // Marker color per model: golden-angle hue over the ranked distinct dtmis,
  // so a model keeps one distinct color (same scheme family as the webclient
  // map/graph, which color by model rank rather than hash).
  const dtmiByTwin = useMemo(
    () => new Map(coreTwins.map((t) => [t.twinId, t.dtmi])),
    [coreTwins],
  );
  const colorByDtmi = useMemo(() => {
    const dtmis = [...new Set(coreTwins.map((t) => t.dtmi))].sort();
    return new Map(dtmis.map((d, i) => [d, `hsl(${Math.round(i * 137.508) % 360} 65% 55%)`]));
  }, [coreTwins]);

  // Model thumbnail per dtmi: core.model_asset_binding names the asset, the
  // assets module's my_asset row carries its thumbnail_url. Either side being
  // unavailable (older core, no assets access) just means plain colored dots.
  const [assetRows] = useTable('assets', assetTables.myAsset);
  const [bindingRows] = useTable('core', coreTables.modelAssetBinding);
  const thumbByDtmi = useMemo(() => {
    const byAssetId = new Map<string, string>();
    for (const a of assetRows) if (a.thumbnailUrl) byAssetId.set(String(a.assetId), a.thumbnailUrl);
    const m = new Map<string, string>();
    for (const b of bindingRows) {
      if (b.assetSource.tag !== 'AssetModule' || b.assetModuleAssetId == null) continue;
      const url = byAssetId.get(String(b.assetModuleAssetId));
      if (url) m.set(b.dtmi.toLowerCase(), url);
    }
    return m;
  }, [assetRows, bindingRows]);

  // Geospatial twin properties (`complex_property` Geospatial values: road
  // centerlines, areas, points), drawn as colored overlays on the 2D map the
  // same way the webclient map page shows them.
  const [complexProps] = useTable('core', coreTables.complexProperty);
  const geoGroups = useMemo<GeoGroup[]>(() => {
    // Group each twin's Geospatial complex_property rows, then decode them with
    // the shared `extractGeoObjects` (the same bindings shape the webclient map
    // uses — Point/Line/Polygon and their Multi* forms, altitude optional). One
    // GeoGroup per twin carries its model colour and tooltip identity, since
    // GeoShapesLayer takes a single colour per instance.
    const byTwin = new Map<string, (typeof complexProps)[number][]>();
    for (const cp of complexProps) {
      if (cp.value.tag !== 'Geospatial') continue;
      const list = byTwin.get(cp.twinId);
      if (list) list.push(cp);
      else byTwin.set(cp.twinId, [cp]);
    }
    const groups: GeoGroup[] = [];
    for (const [twinId, rows] of byTwin) {
      const geoObjects = extractGeoObjects(rows);
      if (!geoObjects.length) continue;
      const dtmi = dtmiByTwin.get(twinId);
      groups.push({
        twinId,
        twinLabel: twinNames.get(twinId),
        color: (dtmi && colorByDtmi.get(dtmi)) || '#e8a33d',
        geoObjects,
      });
    }
    return groups;
  }, [complexProps, dtmiByTwin, colorByDtmi, twinNames]);

  // twinId → its geo property names, for HighlightGeo authoring: filters its
  // Target-twin list to twins that actually have one, and fills its
  // property dropdown from the selected twin's own names.
  const geoPropsByTwin = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const cp of complexProps) {
      if (cp.value.tag !== 'Geospatial') continue;
      const list = m.get(cp.twinId) ?? [];
      if (!list.includes(cp.name)) list.push(cp.name);
      m.set(cp.twinId, list);
    }
    for (const list of m.values()) list.sort();
    return m;
  }, [complexProps]);

  // twinId -> ALL its property names (every twin_property row), for ShowTwinInfo's
  // Properties picker. Unlike geoPropsByTwin (geo-only) this lists every property.
  const propsByTwin = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const tp of twinPropRows) {
      const list = m.get(tp.twinId) ?? [];
      if (!list.includes(tp.name)) list.push(tp.name);
      m.set(tp.twinId, list);
    }
    for (const list of m.values()) list.sort();
    return m;
  }, [twinPropRows]);

  // twinId -> its telemetry names (twin_telemetry_link rows), for ShowTwinInfo's
  // Telemetries picker.
  const telemetryNamesByTwin = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const tl of twinTelemetryRows) {
      const list = m.get(tl.twinId) ?? [];
      if (!list.includes(tl.telemetryName)) list.push(tl.telemetryName);
      m.set(tl.twinId, list);
    }
    for (const list of m.values()) list.sort();
    return m;
  }, [twinTelemetryRows]);

  // twinId -> its LineString geospatial properties as ready-to-use waypoint
  // lists, for the "Fly along path" command's Twin source: pick such a twin and
  // its spline (with per-vertex altitude) is imported as the path. Built from
  // the same complex_property rows the geoGroups decode.
  const lineStringsByTwin = useMemo(() => {
    const m = new Map<string, { name: string; points: CommandLocation[] }[]>();
    for (const cp of complexProps) {
      if (cp.value.tag !== 'Geospatial') continue;
      const geo = cp.value.value;
      if (geo.tag !== 'LineString') continue;
      const points: CommandLocation[] = geo.value.coordinates.map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
        altitude: p.altitude ?? undefined,
      }));
      if (points.length < 2) continue; // not a flyable path
      const list = m.get(cp.twinId) ?? [];
      list.push({ name: cp.name, points });
      m.set(cp.twinId, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [complexProps]);

  // Twins with a Dynamic location mode — the only valid Follow targets (a
  // static twin never moves, so following it is a no-op).
  const dynamicTwinIds = useMemo(
    () => new Set(twinMeta.filter((m) => m.locationMode.tag === 'Dynamic').map((m) => m.twinId)),
    [twinMeta],
  );

  // Camera-target pre-validation (#77): which twins the viewer can actually
  // resolve to a position — a geo location (turbotwin_metadata) or a model
  // asset bound to their dtmi. A site ROOT / grouping twin has neither, and a
  // camera verb aimed at it is a silent no-op here and a forever-wedged
  // control lock in the Unreal runtime. `knownTwinIds` scopes the guard: only
  // a twin core KNOWS but cannot place is a confirmed dead end (an id core
  // hasn't delivered yet fails open — see cameraTargetIssue).
  const knownTwinIds = useMemo(() => new Set(coreTwins.map((t) => t.twinId)), [coreTwins]);
  const flyableTwinIds = useMemo(() => {
    const boundDtmis = new Set(bindingRows.map((b) => b.dtmi.toLowerCase()));
    const s = new Set<string>();
    for (const m of twinMeta) {
      if (m.geoLocationLatitude != null && m.geoLocationLongitude != null) s.add(m.twinId);
    }
    for (const t of coreTwins) if (boundDtmis.has(t.dtmi.toLowerCase())) s.add(t.twinId);
    return s;
  }, [twinMeta, coreTwins, bindingRows]);

  // A twin is a "truck" by dtmi convention (dtmi:…:HaulTruck;1, WaterTruck;1,
  // …) — deployment-agnostic, since there is no vehicle type flag. Trucks are
  // drawn as live-moving glyphs (truckMovers below), not static context dots.
  const isTruckDtmi = (dtmi?: string): boolean => !!dtmi && /truck/i.test(dtmi);

  const twinMarkers = useMemo<TwinMarker[]>(
    () => twinMeta.flatMap((m) => {
      if (m.geoLocationLatitude == null || m.geoLocationLongitude == null) return [];
      const dtmi = dtmiByTwin.get(m.twinId);
      // Trucks render in the moving layer instead (avoid a duplicate dot).
      if (isTruckDtmi(dtmi)) return [];
      return [{
        twinId: m.twinId,
        label: twinNames.get(m.twinId),
        lat: m.geoLocationLatitude,
        lon: m.geoLocationLongitude,
        color: (dtmi && colorByDtmi.get(dtmi)) || '#e8a33d',
        thumbnailUrl: dtmi ? thumbByDtmi.get(dtmi.toLowerCase()) : undefined,
      }];
    }),
    [twinMeta, dtmiByTwin, colorByDtmi, thumbByDtmi, twinNames],
  );

  // Truck twins as live-moving markers: latest telemetry position + heading
  // when a stream row exists, else the static geo location (truck still shows,
  // just parked). Only trucks with some resolvable position are emitted.
  const truckMovers = useMemo<MoverMarker[]>(
    () => twinMeta.flatMap((m) => {
      const dtmi = dtmiByTwin.get(m.twinId);
      if (!isTruckDtmi(dtmi)) return [];
      const stream = streamByTwin.get(m.twinId);
      const live = stream ? posByStream.get(stream) : undefined;
      const lat = live ? live.lat : m.geoLocationLatitude ?? undefined;
      const lon = live ? live.lon : m.geoLocationLongitude ?? undefined;
      if (lat == null || lon == null) return [];
      return [{
        twinId: m.twinId,
        label: twinNames.get(m.twinId),
        lat,
        lon,
        color: (dtmi && colorByDtmi.get(dtmi)) || '#e8a33d',
        heading: live ? live.heading : undefined,
      }];
    }),
    [twinMeta, dtmiByTwin, streamByTwin, posByStream, colorByDtmi, twinNames],
  );

  // Prefer the draft for the selected step so unsaved edits show everywhere.
  const stepOf = (id: string): EditorStep | null =>
    (draft && draft.stepId === id ? draft : model.steps.get(id) ?? null);

  const selStep = selectedStepId ? stepOf(selectedStepId) : null;
  const beats = selStep ? selStep.scripts[EDIT_VERSION] ?? [] : [];
  const selectedAction =
    selAction && beats[selAction.bi] ? beats[selAction.bi].actions[selAction.ai] ?? null : null;
  // Stable identity of the SELECTION (not the action object, which is recloned
  // on every draft edit) — keys the inspector's scroll-into-view.
  const selActionKey =
    selAction && selectedAction ? `${selectedStepId}|${selAction.bi}|${selAction.ai}` : null;
  const selectedCheck = selCheck != null ? selStep?.checks[selCheck] ?? null : null;
  const selCheckKey = selectedCheck ? `${selectedStepId}|check|${selectedCheck.checkId}` : null;

  // A different (or no) action/stop selected: the armed pick lost its target.
  useEffect(() => { setPickTarget((t) => (t === 'action' || t === 'pathNode' ? null : t)); setActivePathIndex(0); }, [selActionKey]);
  useEffect(() => { setPickTarget((t) => (t === 'stop' ? null : t)); }, [selectedStepId]);
  // Legacy timed slots that still hold a script for this stop. The editor never
  // shows or saves them; the Inspector surfaces the list so an author whose
  // Default timeline is empty understands why (Phase 4 migration pending).
  const legacyVersions = selStep ? legacySlotsWithScript(selStep) : [];

  const stopRows = useMemo(() => orderedStepIds.flatMap((id) => {
    const s = stepOf(id);
    if (!s) return [];
    const b = s.scripts[EDIT_VERSION] ?? [];
    return [{ stepId: id, name: s.name, hasScript: b.length > 0, beatCount: b.length, actionCount: b.reduce((n, x) => n + x.actions.length, 0), checkCount: s.checks.length, usedIn: model.usage.get(id) ?? 0, lat: s.lat, lon: s.lon, el: s.el, dwellMinutes: s.durationMinutes }];
  }), [orderedStepIds, model.steps, model.usage, draft]);

  const mapStops: StepMapStop[] = useMemo(() => orderedStepIds.flatMap((id, index) => {
    const s = stepOf(id);
    return s ? [{ stepId: id, name: s.name, lat: s.lat, lon: s.lon, el: s.el, index }] : [];
  }), [orderedStepIds, model.steps, draft]);


  // Imperative handle to the 3D viewer (P2 capture). Null unless the 3D pane
  // is mounted (viewMode === '3d').
  const viewerRef = useRef<ViewerPaneHandle>(null);
  // The viewer's protocol level (0 while booting) gates the measure tool (33);
  // its last `measureChanged` feeds the readout.
  const [viewerLevel, setViewerLevel] = useState(0);
  const [measureSlot, setMeasureSlot] = useState<HTMLSpanElement | null>(null);
  const [measureMsg, setMeasureMsg] = useState<MeasureChanged | null>(null);

  // Last twin the author clicked in the 3D scene (the viewer posts `selected`
  // on every pick; ViewerPane forwards it here). State, not a ref, so the
  // "new beats will target …" hint re-renders live on each pick. It only SEEDS
  // a freshly-added twin-targeting command (see `defaultTwin`) — a pick never
  // retargets an EXISTING beat.
  const [selected3dTwin, setSelected3dTwin] = useState<string | null>(null);

  // The last location authored by an in-scene gesture (pick, placement,
  // place-at-camera). The 3D fly-to below skips a target matching it, so
  // placing a point never yanks the camera away from where the author is
  // working — the coords rule, so no clearing is needed: any OTHER target
  // (different stop/action selected) flies as usual.
  const placedRef = useRef<{ lat: number; lon: number } | null>(null);

  // The selected geo command's location (its own coords, falling back to the
  // stop's like the Inspector fields do). Shown in BOTH panes: the 2D target
  // marker and the 3D fly-to below. null when the selection carries no location.
  const cmdGeo =
    selectedAction?.kind === 'command'
      ? commandLocation(selectedAction, selStep?.lat ?? 0, selStep?.lon ?? 0, selStep?.el ?? 0)
      : null;

  // The selected command's target TWIN, glow-highlighted in 3D on selection — the
  // twin counterpart to cmdGeo/highlightGeo, so selecting a twin-targeted command
  // reveals which twin it acts on (symmetric with the location marker). EXCLUDES
  // FlyToTwin: the camera flies straight to that actor, so glowing it is redundant
  // and the author found it distracting when previewing the fly-to.
  const cmdTwin =
    selectedAction?.kind === 'command' && selectedAction.commandType !== 'FlyToTwin'
      ? commandTargetTwin(selectedAction)
      : null;

  // The selected command's location targets split for the two 3D markers: `look`
  // (orange highlightGeo) and `fly` (green highlightFly). Fly + look shows both;
  // a single-geo command fills only `look`. cmdGeo above stays the representative
  // point (look ?? fly) for the camera focus and the 2D map marker.
  const markers =
    selectedAction?.kind === 'command'
      ? commandGeoMarkers(selectedAction, selStep?.lat ?? 0, selStep?.lon ?? 0, selStep?.el ?? 0)
      : { look: null, fly: null };

  // Fly-to (P2.1): the geo point the 3D camera should look at — the selected
  // geo COMMAND's location when one is selected, else the selected stop's
  // draft-preferred coords. Integer-encoded per the viewer's wire rule
  // (latE7/lonE7 = deg×1e7, elevMilli/rangeMilli = m×1000). null when 2D or
  // nothing selected. ~250 m orbit radius; heading/pitch left absent so the
  // camera keeps its current framing. Keyed on the primitive coords so a
  // stable selection doesn't churn a new object (and re-fly) every render.
  // The orange 3D marker = the selected command's LOOK location (for a single-geo
  // command, its one location). Integer-encoded per the viewer's wire rule.
  const highlightGeo = useMemo(
    () =>
      markers.look
        ? {
            latE7: Math.round(markers.look.lat * 1e7),
            lonE7: Math.round(markers.look.lon * 1e7),
            elevMilli: Math.round(markers.look.el * 1000),
          }
        : null,
    [markers.look?.lat, markers.look?.lon, markers.look?.el],
  );

  // The green 3D marker = the Fly + look FLY location (null for other commands).
  const highlightFly = useMemo(
    () =>
      markers.fly
        ? {
            latE7: Math.round(markers.fly.lat * 1e7),
            lonE7: Math.round(markers.fly.lon * 1e7),
            elevMilli: Math.round(markers.fly.el * 1000),
          }
        : null,
    [markers.fly?.lat, markers.fly?.lon, markers.fly?.el],
  );

  // The authored fly-along path in 3D: the LINE (ground track) plus START (green)
  // and END (red) point markers that follow the flight DIRECTION (they swap when
  // the command is reversed). null for any non-path command (clears all three).
  const pathOverlay = useMemo<SetOverlayArgs[] | null>(() => {
    if (selectedAction?.kind !== 'command' || !isPathCommand(selectedAction)) return null;
    const pts = flightPoints(selectedAction); // flight order (reversed if `reverse`)
    if (pts.length < 2) return null;
    const el = selStep?.el ?? 0;
    const at = (p: { latitude: number; longitude: number; altitude?: number }) => ({
      latE7: Math.round(p.latitude * 1e7),
      lonE7: Math.round(p.longitude * 1e7),
      elevMilli: Math.round((p.altitude ?? el) * 1000),
    });
    const start = pts[0];
    const end = pts[pts.length - 1];
    return [
      { id: 'oe-fly-path', kind: 'line', coords: pts.map(at), style: { color: '#e8a33d', widthPx: 4 } },
      { id: 'oe-fly-path-start', kind: 'point', coords: [at(start)], style: { color: '#3fb950', pointSizePx: 6, marker: 'arrow' }, label: 'Start' },
      { id: 'oe-fly-path-end', kind: 'point', coords: [at(end)], style: { color: '#f85149', pointSizePx: 6, marker: 'arrow' }, label: 'End' },
    ];
  }, [selectedAction, selStep?.el]);

  const focusGeo = useMemo<FlyToGeoArgs | null>(() => {
    // Only a selected COMMAND's location focuses the 3D camera (feature #11).
    // Selecting a STOP no longer moves the camera at all — the author asked for the
    // free-look behaviour in every mode (in orbit the stop-centre fly was intrusive;
    // free-look already ignored it because its rig is suspended). So there is no
    // `?? selStep` fallback here.
    // While (and just after) the orientation is playing, the beat cameras own the
    // view — a focus fly here would fight them; focusSuppressed gates it until the
    // author next selects an action.
    if (viewMode !== '3d' || focusSuppressed) return null;
    // A "Fly along path" command must NOT yank the camera to its first waypoint
    // on selection (it reads as a camera move the author didn't ask for). The
    // route is already shown via the path overlay; leave the camera where it is.
    if (selectedAction?.kind === 'command' && isPathCommand(selectedAction)) return null;
    const t = cmdGeo;
    if (!t) return null;
    const p = placedRef.current;
    if (p && Math.abs(p.lat - t.lat) < 1e-7 && Math.abs(p.lon - t.lon) < 1e-7) return null;
    return {
      latE7: Math.round(t.lat * 1e7),
      lonE7: Math.round(t.lon * 1e7),
      elevMilli: Math.round(t.el * 1000),
      rangeMilli: 250_000,
    };
  }, [viewMode, focusSuppressed, cmdGeo?.lat, cmdGeo?.lon, cmdGeo?.el]);

  // Ghost markers (P2): EVERY stop in the orientation, so the whole tour is
  // visible in 3D (not just the one the camera flew to). Sent with ordered:true
  // (see ViewerPane), so the viewer draws them as numbered orange spikes joined
  // by a dashed follow-line, drawn on top of geometry — matching the 2D path.
  // CSV, one `latE7,lonE7,elevMilli` per line, draft-preferred coords; '' when
  // 2D so the viewer clears them. Keyed like mapStops (draft + model.steps).
  const stopsCsv = useMemo(() => {
    if (viewMode !== '3d') return '';
    return orderedStepIds
      .flatMap((id) => {
        const s = stepOf(id);
        return s ? [`${Math.round(s.lat * 1e7)},${Math.round(s.lon * 1e7)},${Math.round(s.el * 1000)}`] : [];
      })
      .join('\n');
  }, [viewMode, orderedStepIds, model.steps, draft]);

  // Whether any stop in this orientation has an authored script — drives the
  // publish-readiness hint. Only the edited slot counts: publishing an
  // orientation whose script still sits in a legacy timed slot is exactly the
  // case the warning is for.
  const hasAnyScript = useMemo(
    () => orderedStepIds.some((id) => (stepOf(id)?.scripts[EDIT_VERSION]?.length ?? 0) > 0),
    [orderedStepIds, model, draft],
  );

  // Checks across the whole orientation: the denominator the passing score is
  // always shown against ("80% · 13/16 correct"), recomputed live as checks are
  // added or removed.
  const orientationCheckCount = useMemo(
    () => orderedStepIds.reduce((n, id) => n + (stepOf(id)?.checks.length ?? 0), 0),
    [orderedStepIds, model, draft],
  );

  /** Gate a navigation that would drop unsaved draft edits. */
  // Auto-apply model: switching stops mid-debounce FLUSHES the pending edit
  // instead of prompting to discard it. A flush that saveStop REFUSES (a check
  // the module would reject) blocks the navigation too: the draft is discarded
  // on the next stop selection, so leaving would throw the whole edit away
  // instead of the toast's "fix it and it commits".
  const guardDirty = (): boolean => (dirty && draft ? saveStop() : true);
  const selectStop = (id: string | null) => {
    if (id === selectedStepId) return;
    if (!guardDirty()) return;
    setFocusSuppressed(false); // a deliberate stop pick re-enables the focus fly
    setSelectedStepId(id);
    setSelAction(null);
    // Inspector and timeline start collapsed (minimal default layout);
    // picking a stop is the moment they earn their space.
    if (id) { expandPanel('inspector'); expandTimeline(); }
  };

  // ---- connection helper -------------------------------------------------
  const getConn = (): MinetwinConn | null => minetwinState.getConnection() as MinetwinConn | null;
  const ensureMinetwin = useEnsureConnection<MinetwinConn>('minetwin');
  // Reducer calls return a Promise that REJECTS with the server's error string.
  // Un-awaited, a refused reducer is invisible and the caller's success toast
  // lies. Callers that care return the reducer promise from `fn`; this awaits
  // it and toasts the real failure.
  //
  // #243: a dropped socket no longer fails the write at once. Connected, it
  // runs now as before; mid-reconnect, it asks for an immediate reconnect and
  // waits up to 10 s, and only then reports that the change was not saved.
  function withConn(fn: (conn: MinetwinConn) => unknown, label: string) {
    const fail = (e: unknown) =>
      toast({ title: label + ' failed', sub: e instanceof Error ? e.message : String(e), kind: 'bad' });
    const run = (conn: MinetwinConn) => {
      try {
        const r = fn(conn);
        if (r instanceof Promise) r.catch(fail);
      } catch (e) { fail(e); }
    };
    const conn = minetwinState.isActive ? getConn() : null;
    if (conn) { run(conn); return; }
    void ensureMinetwin().then((live) => {
      if (live) run(live);
      else toast({
        title: label + ' not saved',
        sub: 'The connection to MineTwin did not come back within 10 seconds. Try again once the connection banner clears.',
        kind: 'bad',
      });
    });
  }

  // ---- draft editing -----------------------------------------------------
  function editStep(mut: (d: EditorStep) => void) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = cloneStep(prev);
      mut(next);
      // Dwell follows the script estimate until the author types their own
      // value: a dwell that matched the old estimate moves to the new one.
      // ponytail: equality heuristic (a typed value equal to the estimate keeps
      // following it); store an explicit flag on the step if that ever matters.
      if (next.durationMinutes === estimateDwellMinutes(prev)) next.durationMinutes = estimateDwellMinutes(next);
      return next;
    });
    setDirty(true);
  }
  const onStepPatch = (p: Partial<Pick<EditorStep, 'name' | 'durationMinutes' | 'lat' | 'lon' | 'el'>>) => editStep((d) => Object.assign(d, p));
  const onActionChange = (next: EditorAction) => {
    if (!selAction) return;
    editStep((d) => { const b = d.scripts[EDIT_VERSION]; if (b?.[selAction.bi]) b[selAction.bi].actions[selAction.ai] = next; });
  };
  // One armed click (either pane) fills the picked location — the selected
  // stop's or the selected geo command's. A 2D click keeps the current
  // altitude/elevation, a 3D pick captures the real one.
  const pickLocation = (lat: number, lon: number, el?: number) => {
    placedRef.current = { lat, lon };
    if (pickTarget === 'stop') {
      onStepPatch({ lat, lon, ...(el != null ? { el } : {}) });
    } else if (pickTarget === 'action' && selectedAction?.kind === 'command') {
      onActionChange({
        ...selectedAction,
        latitude: lat,
        longitude: lon,
        ...(el != null ? { altitude: el } : {}),
      });
    } else if (pickTarget === 'pathNode' && selectedAction?.kind === 'command') {
      // Fill one waypoint of a multi-location FlyToLocation. A 2D click has no
      // elevation — keep the node's previous altitude then.
      const pts = commandPoints(selectedAction);
      const i = Math.max(0, Math.min(activePathIndex, pts.length - 1));
      const next = pts.slice();
      next[i] = { latitude: lat, longitude: lon, altitude: el ?? next[i]?.altitude };
      onActionChange(withCommandPoints(selectedAction, next));
    } else if ((pickTarget === 'flyTarget' || pickTarget === 'lookAtTarget') && selectedAction?.kind === 'command') {
      // Fill a Fly + look Location target. A 2D click has no elevation — keep
      // the target's previous altitude then (same rule as the branches above).
      const prev = pickTarget === 'flyTarget' ? selectedAction.flyTarget : selectedAction.lookAtTarget;
      const t = {
        tag: 'Location' as const,
        value: { latitude: lat, longitude: lon, altitude: el ?? (prev?.tag === 'Location' ? prev.value.altitude : undefined) },
      };
      onActionChange(pickTarget === 'flyTarget'
        ? { ...selectedAction, flyTarget: t }
        : { ...selectedAction, lookAtTarget: t });
    }
    setPickTarget(null);
  };
  // 3D-only shortcut while a picker is armed: use the CAMERA's own position
  // (not a clicked point) as the location — placing "where I'm standing".
  const placeAtCamera = async () => {
    const v = viewerRef.current;
    if (!v) return;
    try {
      const pose = await v.getCameraPose();
      if (placingStop) placeNewStop(pose.lat, pose.lon, pose.elev);
      else pickLocation(pose.lat, pose.lon, pose.elev);
    } catch (e) {
      toast({ title: 'Could not read the camera location', sub: e instanceof Error ? e.message : String(e), kind: 'bad' });
    }
  };
  // Fill ONE waypoint of a multi-location FlyToLocation from the camera's own
  // position (the "Camera" vertex source per node). Writes the node directly, so
  // it doesn't disturb the armed ground-pick target.
  const placePathNodeAtCamera = async (index: number) => {
    const v = viewerRef.current;
    if (!v || selectedAction?.kind !== 'command') return;
    try {
      const pose = await v.getCameraPose();
      const pts = commandPoints(selectedAction);
      const i = Math.max(0, Math.min(index, pts.length - 1));
      const next = pts.slice();
      next[i] = { latitude: pose.lat, longitude: pose.lon, altitude: pose.elev };
      onActionChange(withCommandPoints(selectedAction, next));
    } catch (e) {
      toast({ title: 'Could not read the camera location', sub: e instanceof Error ? e.message : String(e), kind: 'bad' });
    }
  };
  // "Capture current view" for Fly + look: read the live 3D camera pose and set
  // BOTH targets as locations — fly = the camera's own position, look = its focus
  // point (the cameraPose reply carries both). The two locations reproduce the
  // exact framing (position + focus = rotation, roll 0), authoring the plan's
  // "fly the viewer camera to a spot, capture that framing, replay it" flow.
  const captureFlyLookView = async () => {
    const v = viewerRef.current;
    if (!v || selectedAction?.kind !== 'command' || selectedAction.commandType !== 'FlyAndLookAt') return;
    try {
      const pose = await v.getCameraPose();
      onActionChange({
        ...selectedAction,
        flyTarget: { tag: 'Location', value: { latitude: pose.lat, longitude: pose.lon, altitude: pose.elev } },
        lookAtTarget: { tag: 'Location', value: { latitude: pose.focusLat, longitude: pose.focusLon, altitude: pose.focusElev } },
      });
      toast({ title: 'Captured the current 3D view', kind: 'good' });
    } catch (e) {
      toast({ title: 'Could not read the camera pose', sub: e instanceof Error ? e.message : String(e), kind: 'bad' });
    }
  };
  const onDeleteAction = () => {
    if (!selAction) return;
    editStep((d) => { d.scripts[EDIT_VERSION]?.[selAction.bi]?.actions.splice(selAction.ai, 1); });
    setSelAction(null);
  };
  const onAddBeat = () => editStep((d) => { (d.scripts[EDIT_VERSION] ??= []).push({ actions: [] }); });
  const onDeleteBeat = (bi: number) => {
    editStep((d) => {
      d.scripts[EDIT_VERSION]?.splice(bi, 1);
      // Checks are keyed by beat INDEX, so removing a beat drops its checks and
      // shifts the ones after it down; leaving them would silently re-attach a
      // check to whatever beat inherited the index.
      d.checks = d.checks
        .filter((c) => c.beat !== bi)
        .map((c) => (c.beat > bi ? { ...c, beat: c.beat - 1 } : c));
    });
    setSelAction(null);
    setSelCheck(null);
  };
  // Check editing. A check belongs to the step (not a script slot) but rides the
  // same draft + auto-apply path as the timeline, so it commits with the stop.
  const onAddCheck = (bi: number) => {
    const ci = selStep?.checks.length ?? 0;
    editStep((d) => { d.checks = [...d.checks, makeCheck(bi)]; });
    setSelAction(null);
    setSelCheck(ci);
    expandPanel('inspector');
  };
  const onCheckChange = (next: EditorCheck) => {
    if (selCheck == null) return;
    editStep((d) => { d.checks = d.checks.map((c, i) => (i === selCheck ? next : c)); });
  };
  const onDeleteCheck = () => {
    if (selCheck == null) return;
    editStep((d) => { d.checks = d.checks.filter((_, i) => i !== selCheck); });
    setSelCheck(null);
  };
  // Target for a freshly-added twin-targeting command. Prefer the twin the
  // author just clicked in the 3D scene (validated against the live core twins
  // so a stale/removed pick falls through), then the first project twin, else
  // empty — never a fake twin id baked into saved actions.
  const defaultTwin = (_d: EditorStep) => {
    if (selected3dTwin && twinOptions.includes(selected3dTwin)) return selected3dTwin;
    return twinOptions[0] ?? '';
  };
  const onAddAction = (bi: number, lane: LaneKey) => {
    // Index the new action will land at (append), so it can be auto-selected:
    // adding must immediately open its editor, not silently grow the cell.
    const target = selStep?.scripts[EDIT_VERSION]?.[bi];
    // Camera, narration and hold play once per beat; the timeline hides the
    // button, this keeps a stale click from doubling the lane.
    if (target && laneTaken(target, lane)) return;
    const ai = target?.actions.length ?? 0;
    editStep((d) => {
      const b = d.scripts[EDIT_VERSION];
      if (!b?.[bi]) return;
      let a: EditorAction;
      if (lane === 'narrate') a = { kind: 'narrate', text: '', audioRef: null };
      else if (lane === 'wait') a = { kind: 'wait', ms: 2000 };
      else if (lane === 'camera') a = makeCommand('FlyToTwin', [defaultTwin(d)], 2500);
      else a = makeCommand('HighlightTwin', [defaultTwin(d)]);
      b[bi].actions.push(a);
    });
    setSelAction({ bi, ai });
    expandPanel('inspector');
  };
  const onMoveAction = (from: ActionSel, toBi: number) => {
    // Dropping back onto the same beat must be a true no-op — going through
    // editStep would falsely mark the draft dirty and clear the selection.
    if (from.bi === toBi) return;
    // Camera, narration and hold play once per beat: a drop that would double
    // one of those lanes in the target beat is refused (the timeline shows no
    // drop target for it either).
    const beats = selStep?.scripts[EDIT_VERSION];
    const moving = beats?.[from.bi]?.actions[from.ai];
    if (moving && beats?.[toBi] && laneTaken(beats[toBi], laneOf(moving))) return;
    editStep((d) => {
      const b = d.scripts[EDIT_VERSION];
      if (!b?.[from.bi] || !b[toBi]) return;
      const [a] = b[from.bi].actions.splice(from.ai, 1);
      b[toBi].actions.push(a);
    });
    setSelAction(null);
  };
  // Drag a beat header to reorder whole beats (draft edit, commits on Save).
  const onMoveBeat = (from: number, insert: number) => {
    if (isNoopMove(from, insert)) return;
    editStep((d) => {
      const b = d.scripts[EDIT_VERSION];
      if (!b) return;
      // Re-index the checks through the same move: the permutation is where each
      // old beat index ends up, so a check follows the beat it was authored on.
      const order = moveItem(b.map((_, i) => i), from, insert);
      const newIndexOf = new Map(order.map((oldIndex, newIndex) => [oldIndex, newIndex]));
      d.checks = d.checks.map((c) => ({ ...c, beat: newIndexOf.get(c.beat) ?? c.beat }));
      d.scripts[EDIT_VERSION] = moveItem(b, from, insert);
    });
    setSelAction(null);
  };
  // Drag the selected stop's marker to reposition it. Deliberately NOT wired
  // to plain map clicks: an accidental click must never move a stop — only a
  // marker drag or an armed pick/placement changes a location.
  //
  // 2D-map placement elevation. A Leaflet click/drag gives lat/lon only — the
  // raster basemap has no height — so a stop placed in 2D would float off the
  // terrain the viewer renders. Sample the SAME terrarium DEM the viewer meshes
  // (elevationAt) and pass the real elevation through the normal place/pick/move
  // paths. A failed lookup resolves null and each callee keeps its old fallback
  // (inherit the seed stop's elevation / keep the current one). The 3D pick
  // paths are unchanged — they already carry a raycast elevation.
  const placeStopFrom2D = (lat: number, lon: number) => {
    void elevationAt(lat, lon).then((el) => placeNewStop(lat, lon, el ?? undefined));
  };
  const pickLocationFrom2D = (lat: number, lon: number) => {
    void elevationAt(lat, lon).then((el) => pickLocation(lat, lon, el ?? undefined));
  };
  const moveStopFrom2D = (lat: number, lon: number) => {
    void elevationAt(lat, lon).then((el) =>
      editStep((d) => { d.lat = lat; d.lon = lon; if (el != null) d.el = el; }),
    );
  };

  // Preview a camera command in the embedded 3D viewer (P2.3): translate an
  // authored `CommandAction` into the matching cinematic bridge verb and fire
  // it, so an author sees the actual camera move without leaving the editor.
  // Only the twin/geo fly+look verbs have a viewer counterpart; Orbit/Follow/
  // Stop have no preview yet (`previewableCommand` gates the button off).
  // Geo commands reuse the stop's coords when the command left lat/lon/alt
  // unset — exactly the inspector's `a.latitude ?? step.lat` fallback.
  // Encode one Fly+look target into `flyToLookAt` fields for its side: a twin
  // becomes `${side}TwinId`; a location becomes the `${side}LatE7/LonE7/ElevMilli`
  // triple (integer E7/milli like flyToGeo), altitude falling back to `fallbackEl`
  // metres (the stop's elevation) when the location has none.
  const flyLookAtSide = (
    t: CommandTarget,
    side: 'fly' | 'look',
    fallbackEl: number,
  ): Partial<FlyToLookAtArgs> => {
    if (t.tag === 'Twin') {
      return side === 'fly' ? { flyTwinId: t.value } : { lookTwinId: t.value };
    }
    const latE7 = Math.round(t.value.latitude * 1e7);
    const lonE7 = Math.round(t.value.longitude * 1e7);
    const elevMilli = Math.round((t.value.altitude ?? fallbackEl) * 1000);
    return side === 'fly'
      ? { flyLatE7: latE7, flyLonE7: lonE7, flyElevMilli: elevMilli }
      : { lookLatE7: latE7, lookLonE7: lonE7, lookElevMilli: elevMilli };
  };

  const previewCommand = (a: CommandAction): void => {
    const v = viewerRef.current;
    if (!v) { toast({ title: 'Switch to 3D to preview a camera move', kind: 'bad' }); return; }
    // Same pre-validation as playback (#77): a camera verb aimed at a twin the
    // viewer cannot place (root/group twins with no geometry or location) is a
    // silent no-op — tell the author why nothing will move instead.
    const targetIssue = cameraTargetIssue(a, { knownTwinIds, flyableTwinIds }, twinNames);
    if (targetIssue) { toast({ title: 'Cannot preview this camera move', sub: targetIssue, kind: 'bad' }); return; }
    const dur = a.durationMs ?? undefined;
    const geo = () => ({
      latE7: Math.round((a.latitude ?? selStep?.lat ?? 0) * 1e7),
      lonE7: Math.round((a.longitude ?? selStep?.lon ?? 0) * 1e7),
      elevMilli: Math.round((a.altitude ?? selStep?.el ?? 0) * 1000),
    });
    // Waypoints for a multi-location FlyToLocation, integer E7/milli like geo().
    // A point with no altitude falls back to the stop's elevation.
    const pathPoints = () =>
      flightPoints(a).map((p) => ({
        latE7: Math.round(p.latitude * 1e7),
        lonE7: Math.round(p.longitude * 1e7),
        // Fly at the command's eye-height offset above each waypoint (default
        // ~1.5 m) — the drawn overlay stays on the ground track, camera rides above.
        elevMilli: Math.round(((p.altitude ?? selStep?.el ?? 0) + (a.eyeHeightOffset ?? CAM_EYE_HEIGHT_M)) * 1000),
      }));
    // Camera verbs drive the ORBIT rig. In free-fly / walk the rig is suspended,
    // so a fly/look preview is invisible AND leaves a stale orbit target that
    // snaps the view (up to ~180°) the next time the author enters orbit — the
    // "stuck then jump" bug. Put the rig in Free Orbit first, exactly as the Play
    // loop and the Orbit/Follow previews already do (idempotent when already
    // orbiting; StopCamera is exempt — it ends a motion, it doesn't start one).
    if (laneOf(a) === 'camera' && a.commandType !== 'StopCamera') v.setCameraMode('freeOrbit');
    let sent: boolean;
    switch (a.commandType) {
      case 'FlyToTwin':
        sent = v.flyToTwin({ twinId: a.twinIds[0], durationMillis: dur });
        break;
      case 'LookAtTwin':
        sent = v.lookAtTwin({ twinId: a.twinIds[0], durationMillis: dur });
        break;
      case 'FlyToLocation':
        sent = v.flyToGeo({ ...geo(), durationMillis: dur });
        break;
      case 'FlyAlongPath':
        sent = v.flyAlongPath({ points: pathPoints(), durationMillis: dur });
        break;
      case 'LookAtLocation':
        sent = v.lookAtGeo({ ...geo(), durationMillis: dur });
        break;
      case 'FlyAndLookAt': {
        const { fly, look } = flyLookTargets(a);
        if (!fly || !look) {
          toast({ title: 'Fly + look needs both a fly and a look target', kind: 'bad' });
          return;
        }
        // Each target is a twin or a geo location; `flyToLookAt` resolves either.
        const fallbackEl = a.altitude ?? selStep?.el ?? 0;
        sent = v.flyToLookAt({
          ...flyLookAtSide(fly, 'fly', fallbackEl),
          ...flyLookAtSide(look, 'look', fallbackEl),
          durationMillis: dur,
        });
        break;
      }
      // Continuous motions: force Free Orbit (the rig they drive), start the
      // motion, and remember which command is running so its button becomes
      // "Stop Preview". They run until `stopPreview` (or an auto-stop edge).
      // Units: `distance` is stored in centimetres (the Unreal / mp contract),
      // and `distanceMilli` is millimetres — so cm -> mm is x10 (NOT x1000).
      // `rotateSpeed` is deg/s and `pitch` is degrees, both -> milli via x1000.
      // Sign: Unreal stores pitch positive-down; the viewer's pitchMilli is
      // negative-down. Send -|pitch| so an authored down-tilt tilts down.
      case 'OrbitTwin':
        v.setCameraMode('freeOrbit');
        sent = v.orbitTwin({
          twinId: a.twinIds[0],
          ...(a.rotateSpeed != null ? { rotateSpeedMilli: Math.round(a.rotateSpeed * 1000) } : {}),
          ...(a.distance != null ? { distanceMilli: Math.round(a.distance * 10) } : {}),
          ...(a.pitch != null ? { pitchMilli: -Math.round(Math.abs(a.pitch) * 1000) } : {}),
        });
        if (sent) beginContinuousPreview(a);
        break;
      case 'FollowTwin':
        v.setCameraMode('freeOrbit');
        sent = v.followTwin({
          twinId: a.twinIds[0],
          ...(a.distance != null ? { distanceMilli: Math.round(a.distance * 10) } : {}),
          ...(a.pitch != null ? { pitchMilli: -Math.round(Math.abs(a.pitch) * 1000) } : {}),
        });
        if (sent) beginContinuousPreview(a);
        break;
      // Marked as a running preview so it clears on navigate-away (and, if the
      // command carries a duration, after it — via beginContinuousPreview).
      case 'HighlightTwin':
      case 'HighlightGeo': {
        // Highlight the twin at its ACTUAL viewer position (mesh centre) via the
        // `highlightTwin` verb — the viewer does the lookup, so the cue lands on
        // the twin rather than its stored geo anchor. Only when the command
        // carries no twin do we fall back to a geo point (the stop).
        const twinId = a.twinIds[0];
        if (twinId) {
          sent = v.highlightTwin(twinId);
        } else {
          const lat = selStep?.lat ?? 0;
          const lon = selStep?.lon ?? 0;
          const el = selStep?.el ?? 0;
          sent = v.highlightGeo({
            latE7: Math.round(lat * 1e7),
            lonE7: Math.round(lon * 1e7),
            elevMilli: Math.round(el * 1000),
          });
        }
        // Auto-clear after the authored duration, defaulting to the runtime's 5 s
        // (UTwinHighlightSubsystem) — like playback and SetTransparency below — so
        // a highlight preview ends on its own instead of holding until the author
        // navigates away. The auto-stop routes through stopPreview, which now
        // clears the twin marker too.
        if (sent) beginContinuousPreview({ ...a, durationMs: a.durationMs ?? 5000 });
        break;
      }
      case 'SelectTwins':
        // Preview the selection outline exactly like playback's SelectTwins.
        sent = v.setSelection(a.twinIds);
        break;
      case 'ClearSelection':
        // Clear the whole selection (empty set), matching playback.
        sent = v.setSelection([]);
        break;
      case 'SetTransparency': {
        // Fade the target twin(s) to the command's opacity (default 0.25, the
        // Unreal `SetCommandTransparency` default). opacityMilli = opacity × 1000
        // (INTEGER wire rule); opacity >= 1 sends 1000 = restore. Tracked as a
        // running preview (beginContinuousPreview) so it auto-reverts after the
        // command's duration and clears on navigate-away — exactly like Highlight.
        const opacity = a.opacity ?? 0.25;
        const opacityMilli = Math.min(1000, Math.max(0, Math.round(opacity * 1000)));
        sent = v.setTwinTransparency(a.twinIds, opacityMilli, a.dither);
        // Auto-revert after the duration the Inspector SHOWS. SetTransparency has
        // no Continuous/Timed toggle, so its duration field always displays a
        // value (defaulting to 2 s) — but `durationMs` stays null until edited.
        // Preview off the shown value so the fade reverts as authored rather than
        // lingering when the field reads "2" but the stored duration is null.
        if (sent) beginContinuousPreview({ ...a, durationMs: a.durationMs ?? 2000 });
        break;
      }
      case 'SetCameraMode': {
        // Preview the navigation-mode switch: map the authoring vocab
        // (freeFly/orbit/walk) to the viewer's (freeLook/freeOrbit/walk) and apply
        // it. Unlike a fly/look preview it is NOT auto-reverted — the author sees
        // the camera behave in the chosen mode until they change it. SetCameraMode
        // is an Effects-lane command, so the one-shot camera restore below (gated
        // on laneOf === 'camera') never runs for it.
        const mode = a.message === 'walk' ? 'walk' : a.message === 'orbit' ? 'freeOrbit' : 'freeLook';
        sent = v.setCameraMode(mode);
        break;
      }
      case 'ShowRelationships':
      case 'HideRelationships': {
        // Preview relationship lines: an empty twin list is the global show-all
        // toggle (setRelationshipLinesVisible); otherwise toggle each listed
        // twin's lines on/off (setRelationships). Effects-lane, so the one-shot
        // camera restore below never runs and the lines persist like a selection.
        // Requires a /bevy carrying the relationship verbs; older viewers drop
        // them silently (the editor still authors/saves fine either way).
        const shown = a.commandType === 'ShowRelationships';
        const twins = a.twinIds;
        sent = twins.length === 0
          ? v.setRelationshipLinesVisible(shown)
          : twins.map((id) => v.setRelationships(id, shown)).every(Boolean);
        // A ShowRelationships preview turns lines ON; mark it running so Stop /
        // navigate-away clears them (relPreviewHideRef) and auto-hide after the
        // command's duration. The duration field always shows a value (defaulting
        // to 2 s, like SetTransparency), so preview off the SHOWN value — keying
        // off `durationMs != null` left the displayed "2 s" with no effect until
        // the field was explicitly edited.
        if (sent && shown) {
          relPreviewHideRef.current = () => {
            const vr = viewerRef.current;
            if (!vr) return;
            if (twins.length === 0) vr.setRelationshipLinesVisible(false);
            else for (const id of twins) vr.setRelationships(id, false);
          };
          setPreviewingKey(selActionKey);
          if (previewTimerRef.current) { clearTimeout(previewTimerRef.current); previewTimerRef.current = null; }
          previewTimerRef.current = setTimeout(() => { previewTimerRef.current = null; stopPreview(); }, a.durationMs ?? 2000);
        }
        break;
      }
      case 'ShowTwinInfo': {
        // Preview the twin info panel: the viewer resolves the chosen property/
        // telemetry VALUES + the twin's screen position and sends `twinInfo`,
        // which ViewerPane draws as a floating card. Effects-lane, so the
        // one-shot camera restore below never runs. Auto-hides after the
        // command's duration (cleared by stopPreview). Needs a /bevy carrying
        // the showTwinInfo verb; an older viewer drops it silently.
        const twinId = a.twinIds[0];
        if (!twinId) { toast({ title: 'Twin info needs a twin', kind: 'bad' }); return; }
        sent = v.showTwinInfo({
          twinId,
          propertyNames: a.propertyNames,
          telemetryNames: a.telemetryNames,
        });
        if (sent) {
          setPreviewingKey(selActionKey);
          if (previewTimerRef.current) { clearTimeout(previewTimerRef.current); previewTimerRef.current = null; }
          previewTimerRef.current = setTimeout(() => { previewTimerRef.current = null; stopPreview(); }, a.durationMs ?? 4000);
        }
        break;
      }
      default:
        toast({ title: `No 3D preview for ${a.commandType}`, kind: 'bad' });
        return;
    }
    if (!sent) { toast({ title: 'Viewer is still loading — try again in a moment', kind: 'bad' }); return; }
    // Mark a one-shot camera preview (fly / look) as running for its flight, so
    // the button shows a Stop and the author can SEE it's previewing. Continuous
    // (Orbit / Follow) and the effect verbs manage their own running state via
    // beginContinuousPreview inside the switch, so skip those here. (StopCamera
    // returned from the switch above, so it can't reach here.)
    if (laneOf(a) === 'camera' && !isContinuousCam(a)) {
      beginOneShotPreview(dur ?? 2500);
    }
  };

  // Start a continuous preview (Orbit / Follow): mark the running command and, if
  // it carries an authored duration, schedule an auto-stop after it — so the
  // preview honours the command's own duration, exactly as the runtime would. A
  // blank duration is truly continuous (see `isContinuousCam`) and runs until the
  // author hits Stop (or an auto-stop edge).
  // A one-shot camera preview (fly / look) has no continuous motion to stop, but
  // the author still wants to see it running and be able to interrupt it. Mark it
  // running for its flight; the auto-clear is LIGHT (the flight is already over,
  // so no stopCamera / marker reset — that would clear the selection markers).
  // An explicit Stop (stopPreview) or navigate-away still does the full stop.
  const beginOneShotPreview = (durationMs: number): void => {
    setPreviewingKey(selActionKey);
    if (previewTimerRef.current) { clearTimeout(previewTimerRef.current); previewTimerRef.current = null; }
    // A fly/look preview transiently forces Free Orbit (the rig the camera verbs
    // drive), but must LEAVE the camera in the behaviour the author picked in
    // Camera Controls — never parked in orbit. Restore that mode once the move
    // has settled (a small buffer past the flight so the ease finishes first).
    // Marker-LIGHT: no stopCamera, so Highlight/Select/Transparency previews
    // survive. Free Orbit -> Free Look/Walk re-seats from the current aimed
    // transform, so a Look-at ends facing the target with no orbit and no snap.
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      const v = viewerRef.current;
      if (v) v.setCameraMode(cameraMode);
      setPreviewingKey(null);
    }, Math.max(600, durationMs) + 250);
  };

  const beginContinuousPreview = (a: CommandAction): void => {
    setPreviewingKey(selActionKey);
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    // Only a TIMED command auto-stops. A continuous one (Follow — always; Orbit
    // when its duration is blank) runs until the author hits Stop. Gate on
    // `isContinuousCam`, NOT a bare `durationMs != null`: Follow's duration field
    // was removed but a stale `durationMs` can still linger in the data (e.g. a
    // Follow once toggled Timed), and keying off it would wrongly auto-stop the
    // follow the instant the stale timer fired.
    if (!isContinuousCam(a) && a.durationMs != null) {
      previewTimerRef.current = setTimeout(() => {
        previewTimerRef.current = null;
        stopPreview();
      }, a.durationMs);
    }
  };

  // Stop a running continuous preview (Orbit / Follow) and restore the author's
  // camera mode. Idempotent — safe to call when nothing is running.
  const stopPreview = (): void => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    const v = viewerRef.current;
    if (v) {
      v.stopCamera();
      v.setCameraMode(cameraMode);
      v.highlightTwin(''); // clear a TWIN highlight preview (the common case) —
      v.highlightGeo(null); // …and a geo one. Both, or a twin highlight lingers.
      v.highlightFly(null); // …and the green fly marker.
      v.setTwinTransparency([], 1000); // restore a transparency preview (no-op otherwise)
      v.showTwinInfo(null); // clear a ShowTwinInfo panel preview (no-op otherwise)
    }
    // Hide the relationship lines a ShowRelationships preview turned on.
    if (relPreviewHideRef.current) { relPreviewHideRef.current(); relPreviewHideRef.current = null; }
    setPreviewingKey(null);
    // Also end a narration-caption or a single-check preview. Guarded on !playing
    // so it never tears down a live Play-orientation run (which owns the same
    // caption / activeCheck state).
    if (!playing) {
      setCaptionPreviewing(false);
      setPlayCaption(null);
      setActiveCheck(null);
    }
  };

  // "Preview in 3D" for a narration line: show its caption over the viewer for
  // the line's estimated spoken length, then auto-stop. (Captions only — TTS is
  // not rendered in preview, same as Play-orientation.)
  const previewNarration = (a: NarrateAction): void => {
    const text = a.text.trim();
    if (!text) { toast({ title: 'This narration has no text to preview', kind: 'bad' }); return; }
    setActiveCheck(null);
    setPlayCaption({ stop: selStep?.name ?? 'Narration', text, progress: '' });
    setCaptionPreviewing(true);
    setPreviewingKey(selActionKey);
    if (previewTimerRef.current) { clearTimeout(previewTimerRef.current); previewTimerRef.current = null; }
    previewTimerRef.current = setTimeout(() => { previewTimerRef.current = null; stopPreview(); }, Math.max(1500, narrMs(text)));
  };

  // "Preview in 3D" for a check: present it exactly as playback does (QuizPanel /
  // the World-selection scene prompt), graded in the browser. Resolving the check
  // (or Stop) clears it. No play loop, so no beat gate — `checkResolve` stays null.
  const previewCheckIn3d = (c: EditorCheck): void => {
    setCaptionPreviewing(false);
    setPlayCaption(null);
    setActiveCheck(c);
    setPreviewingKey(selCheckKey);
  };

  // Auto-stop the continuous preview the moment the author navigates away from
  // it — selecting another action, switching camera mode, or leaving 3D — so a
  // motion never keeps spinning after its command is no longer in focus. Reads
  // the live key through a ref so starting a preview (which changes none of the
  // deps) doesn't immediately cancel itself.
  const previewingKeyRef = useRef<string | null>(null);
  previewingKeyRef.current = previewingKey;
  useEffect(() => {
    if (!previewingKeyRef.current) return;
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    const v = viewerRef.current;
    if (v) {
      v.stopCamera();
      v.setCameraMode(cameraMode);
      v.highlightTwin(''); // clear a TWIN highlight preview on navigate-away —
      v.highlightGeo(null); // …and a geo one. Both, or a twin highlight lingers.
      v.highlightFly(null); // …and the green fly marker.
      v.setTwinTransparency([], 1000); // restore a transparency preview on navigate-away
      v.showTwinInfo(null); // clear a ShowTwinInfo panel on navigate-away
    }
    setPreviewingKey(null);
  }, [selActionKey, cameraMode, viewMode]);

  // Clear any pending auto-stop timer on unmount so it can't fire into a gone tree.
  useEffect(() => () => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
  }, []);

  // ---------------------------------------------------------------------------
  // Play the WHOLE orientation in the 3D viewer (PLAY-ORIENTATION-IN-WEB): an
  // author-facing walkthrough so an orientation can be checked without launching
  // Unreal. Sequence every stop's beats in order, fire each beat's camera verb
  // through the same cinematic bridge the per-command preview uses, show the
  // narration as a timed caption, and hold each beat for the model's own duration
  // estimate (beatDriver) — the identical timing the runtime derives. Covers
  // camera choreography, narration captions, and the Effects lane that has a
  // viewer verb: Highlight (pulsing marker) and Transparency (twin fade). Select
  // is captioned only, and voice/TTS is not rendered.
  // State (playing / playCaption / playPos) and the cancellation ref are declared
  // up with the other UI state so `focusGeo` can suppress its focus fly while a
  // run is live. "Anything to play?" reuses `hasAnyScript` (above).

  // Fire a beat's camera command (if any) into the viewer, reusing the exact verb
  // mapping and unit conversions of `previewCommand`. Returns whether it started a
  // CONTINUOUS motion (Orbit/Follow) the loop must stop before the next beat, and
  // the animation time it kicked off (`ms`) so the loop holds exactly that long. A
  // fly/look with no authored duration gets DEFAULT_CAM_MS — otherwise the loop's
  // floor would cut the default fly short and the beat flashes past.
  const DEFAULT_CAM_MS = 2500;
  // Cinematic downward tilt for fly-to moves. The viewer keeps the camera's
  // CURRENT pitch when a fly verb omits one, so a fly from the default top-down
  // orbit stays top-down (unlike Unreal, which frames the twin from an oblique
  // angle). Send an explicit tilt so the flight tips into a natural view. Units:
  // protocol pitchMilli = tilt° × 1000, negative = looking down (viewer negates
  // it into a raised-camera pitch); −25° reads well without clipping terrain.
  const PLAY_TILT_MILLI = -25_000;
  const fireBeatCamera = (v: ViewerPaneHandle, beat: Beat, step: EditorStep): { continuous: boolean; ms: number; hadCamera: boolean; skipped?: string } => {
    playRef.current.recam = null; // clear the previous beat's mid-beat re-pace hook
    let cam: CommandAction | null = null;
    for (const a of beat.actions) if (a.kind === 'command' && laneOf(a) === 'camera') cam = a;
    if (!cam) return { continuous: false, ms: 0, hadCamera: false };
    const c = cam;
    // Pre-validation (#77): a camera verb aimed at a twin the viewer cannot
    // place (the site root / a grouping twin with no geometry or location — the
    // exact fly_target that wedges the Unreal runtime's control lock forever)
    // is a silent no-op. Skip the move and report it so the loop shows the
    // author a real message instead of holding a flight that never happens;
    // the beat still advances on its own (bounded) timing.
    const issue = cameraTargetIssue(c, { knownTwinIds, flyableTwinIds }, twinNames);
    if (issue) return { continuous: false, ms: 0, hadCamera: false, skipped: issue };
    const dur = c.durationMs ?? DEFAULT_CAM_MS; // effective travel time we HOLD for (unscaled)
    // Play-speed scales the camera MOTION host-side: the viewer runs the move over
    // `sentDur` (= dur / speed), so a 10 s fly at 2x is a 5 s move. We still return
    // `ms: dur` (unscaled) so playHoldMs + holdMs scale the beat hold exactly once —
    // the hold's camera term and the move then both come out to dur / speed.
    const sentDur = Math.max(0, Math.round(dur / (playSpeedRef.current || 1)));
    // Re-pace the IN-FLIGHT move when the play-speed changes mid-beat: re-fire the
    // SAME verb with the remaining time rescaled, so a fly/look continues to its
    // target faster/slower from wherever the camera is. `armCam` fires the move now
    // and installs the re-pace hook (`recam`, called by the play-speed effect). Not
    // used for flyAlongPath (a spline re-fire would jump) or continuous Orbit/Follow.
    let camReal = sentDur, camAt = Date.now(), camSpd = playSpeedRef.current || 1;
    const armCam = (fire: (durMs: number) => void) => {
      fire(camReal);
      playRef.current.recam = () => {
        const spd = playSpeedRef.current || 1;
        const left = Math.max(0, camReal - (Date.now() - camAt)); // real ms left at the old pace
        if (left <= 0) { playRef.current.recam = null; return; }   // move already arrived
        camReal = Math.max(0, Math.round((left * camSpd) / spd));  // same distance, new pace
        camAt = Date.now();
        camSpd = spd;
        fire(camReal);
      };
    };
    const geo = () => ({
      latE7: Math.round((c.latitude ?? step.lat ?? 0) * 1e7),
      lonE7: Math.round((c.longitude ?? step.lon ?? 0) * 1e7),
      elevMilli: Math.round((c.altitude ?? step.el ?? 0) * 1000),
    });
    const pathPoints = () =>
      flightPoints(c).map((p) => ({
        latE7: Math.round(p.latitude * 1e7),
        lonE7: Math.round(p.longitude * 1e7),
        // Fly at the command's eye-height offset above each waypoint (default ~1.5 m).
        elevMilli: Math.round(((p.altitude ?? step.el ?? 0) + (c.eyeHeightOffset ?? CAM_EYE_HEIGHT_M)) * 1000),
      }));
    // Re-assert freeOrbit for EVERY camera verb, not just Orbit/Follow. The play
    // loop sets freeOrbit once at start, but a WORLD-SELECTION check parks the
    // camera in freeLook (so the trainee can fly to hunt the twin) and never
    // restores it — after that beat a FlyTo/LookAt/FlyToLocation/FlyAndLookAt
    // would drive the orbit rig while it's parked in free-look and be swallowed
    // ("fly to doesn't work after the check"). Un-park here so the verb lands.
    v.setCameraMode('freeOrbit');
    switch (c.commandType) {
      case 'FlyToTwin': armCam((d) => v.flyToTwin({ twinId: c.twinIds[0], pitchMilli: PLAY_TILT_MILLI, durationMillis: d })); return { continuous: false, ms: dur, hadCamera: true };
      case 'LookAtTwin': armCam((d) => v.lookAtTwin({ twinId: c.twinIds[0], durationMillis: d })); return { continuous: false, ms: dur, hadCamera: true };
      case 'FlyToLocation':
        armCam((d) => v.flyToGeo({ ...geo(), pitchMilli: PLAY_TILT_MILLI, durationMillis: d }));
        return { continuous: false, ms: dur, hadCamera: true };
      case 'FlyAlongPath':
        v.flyAlongPath({ points: pathPoints(), durationMillis: sentDur });
        return { continuous: false, ms: dur, hadCamera: true };
      case 'LookAtLocation': armCam((d) => v.lookAtGeo({ ...geo(), durationMillis: d })); return { continuous: false, ms: dur, hadCamera: true };
      case 'FlyAndLookAt': {
        const { fly, look } = flyLookTargets(c);
        if (fly && look) {
          const fallbackEl = c.altitude ?? step.el ?? 0;
          armCam((d) => v.flyToLookAt({
            ...flyLookAtSide(fly, 'fly', fallbackEl),
            ...flyLookAtSide(look, 'look', fallbackEl),
            durationMillis: d,
          }));
        }
        return { continuous: false, ms: dur, hadCamera: true };
      }
      case 'OrbitTwin':
        // Always send a downward tilt: an orbit with no authored pitch would keep
        // the (top-down) starting pitch and spin flat — the "camera never rotates"
        // symptom. Default to the same cinematic tilt as the fly-tos.
        // Sign: Unreal stores the orbit pitch positive-down, but the viewer's
        // pitchMilli is negative-down (see PLAY_TILT_MILLI). Send -|pitch| so an
        // authored 45° tilts the orbit DOWN, not up into the sky / flat top-down.
        // Re-assert freeOrbit (see FollowTwin below): un-parks the orbit controller
        // a prior fly-to may have disabled, so the motion's writes take effect.
        v.setCameraMode('freeOrbit');
        v.orbitTwin({
          twinId: c.twinIds[0],
          ...(c.rotateSpeed != null ? { rotateSpeedMilli: Math.round(c.rotateSpeed * 1000) } : {}),
          ...(c.distance != null ? { distanceMilli: Math.round(c.distance * 10) } : {}),
          pitchMilli: c.pitch != null ? -Math.round(Math.abs(c.pitch) * 1000) : PLAY_TILT_MILLI,
        });
        return { continuous: true, ms: c.durationMs ?? 0, hadCamera: true };
      case 'FollowTwin':
        // Follow tracks a MOVING twin: recentre the pivot on it every frame so the
        // camera rides along as it drives (a Dynamic twin with live telemetry moves
        // in the preview too, not just at runtime). This mirrors previewCommand's
        // followTwin — the earlier one-shot flyToTwin substitution here flew to the
        // twin's START position and held, so a moving twin drove out of frame and it
        // read as "the twin moves, the camera stays" (Preview worked, Play didn't).
        // Continuous like Orbit (Follow is an orbit at rate 0): the loop stops it at
        // the next camera command / stop boundary. Pitch sign matches OrbitTwin —
        // both feed the same apply path, whose pitchMilli is negative-down — so send
        // -|pitch|, defaulting to the cinematic tilt when the beat authored none.
        // Re-assert freeOrbit exactly like previewCommand: a prior beat (a fly-to)
        // PARKS the orbit controller (bevy `park_orbit` -> panorbit.enabled=false)
        // so its direct-mode flight owns the transform. The play loop only sets
        // freeOrbit ONCE at the start, so after such a beat the follow's per-frame
        // pivot writes hit a disabled controller and the camera sits parked — the
        // "Follow works on Preview, dead in Play" bug. setCameraMode un-parks it.
        v.setCameraMode('freeOrbit');
        v.followTwin({
          twinId: c.twinIds[0],
          ...(c.distance != null ? { distanceMilli: Math.round(c.distance * 10) } : {}),
          pitchMilli: c.pitch != null ? -Math.round(Math.abs(c.pitch) * 1000) : PLAY_TILT_MILLI,
        });
        // Follow is ALWAYS continuous — never a fixed ms. The beat's own timing
        // (narration / hold) drives how long it holds; the loop stops it at the
        // next camera command or stop boundary.
        return { continuous: true, ms: 0, hadCamera: true };
      case 'StopCamera': v.stopCamera(); return { continuous: false, ms: 0, hadCamera: true };
      default: return { continuous: false, ms: 0, hadCamera: false };
    }
  };

  // Cancellable hold: resolves after `ms` of BEAT-TIME, or immediately when Stop
  // wakes it. Beat-time is scaled by the live play-speed (so the whole orientation
  // runs faster / slower) in lock-step with the viewer's camera clock
  // (setPlaybackSpeed). A live speed change re-times the in-flight hold via
  // `reschedule`: the beat-time already consumed at the old speed is subtracted and
  // the remainder re-scheduled at the new speed, so a mid-beat change takes effect
  // on the CURRENT beat.
  const holdMs = (ms: number) => new Promise<void>((resolve) => {
    const s = playRef.current;
    let remaining = ms;            // beat-time still to elapse
    let scheduledAt = Date.now();  // real time the current timer was (re)scheduled
    let speedUsed = playSpeedRef.current || 1;
    const finish = () => { s.timer = null; s.wake = null; s.reschedule = null; resolve(); };
    const schedule = () => {
      scheduledAt = Date.now();
      speedUsed = playSpeedRef.current || 1;
      s.timer = setTimeout(finish, Math.max(0, Math.round(remaining / speedUsed)));
    };
    s.reschedule = () => {
      if (!s.timer) return;
      clearTimeout(s.timer);
      remaining = Math.max(0, remaining - (Date.now() - scheduledAt) * speedUsed); // consumed beat-time
      schedule();
    };
    s.wake = () => { if (s.timer) { clearTimeout(s.timer); s.timer = null; } s.wake = null; s.reschedule = null; resolve(); };
    schedule();
  });

  // --- Interactive stop gate (Unreal-style walk-to-marker) --------------------
  // Each stop shows its tube marker; the loop awaits the author flying in
  // (`reached` from the viewer) and confirming Start/Advance. `onStopMarkerLeft`
  // just disables the confirm again (they stepped out of the tube).
  const awaitStopConfirm = () => new Promise<void>((resolve) => { stopConfirmRef.current = resolve; });
  const onStopMarkerReached = () => setStopGate((g) => (g ? { ...g, reached: true } : g));
  const onStopMarkerLeft = () => setStopGate((g) => (g ? { ...g, reached: false } : g));
  const confirmStopGate = () => { const r = stopConfirmRef.current; stopConfirmRef.current = null; r?.(); };
  // "Fly To" the active stop marker — lands the camera close enough to trip the
  // viewer's enter-trigger (like Unreal's fly-in point inside the tube).
  const flyToStopMarker = () => {
    const v = viewerRef.current;
    const step = stopGate ? stepOf(stopGate.stepId) : null;
    if (!v || !step) return;
    // Fly to each stop's START POINT LEVELED TO THE HORIZON (pitch 0), mirroring
    // Samuel's Unreal fly-to-marker fix so arriving at a stop never faces down into
    // the marker geometry. Land the camera INSIDE the marker's enter-trigger so it
    // turns green reliably: the Bevy `stop_marker` trigger fires only within
    // MARKER_RADIUS (5 m) horizontally, and a PanOrbitCamera at range r sits
    // r·cos(pitch) out — at pitch 0 that is the full range, so range 3.5 m keeps the
    // horizontal offset (3.5 m) safely under 5 m while the view stays level.
    // Lift the camera a little ABOVE the stop's ground elevation so it does not land
    // right on the deck — kept to ~standing eye height so it still fits inside a tight
    // tunnel (~2 m of headroom) instead of clipping through the ceiling.
    const STOP_CAM_LIFT_MM = CAM_EYE_HEIGHT_M * 1000; // shared eye height
    // flyToGeo drives the orbit rig, which ignores fly commands while the camera
    // is in freeLook (#228): re-assert freeOrbit first, as fireBeatCamera does.
    v.setCameraMode('freeOrbit');
    v.flyToGeo({
      latE7: Math.round((step.lat ?? 0) * 1e7),
      lonE7: Math.round((step.lon ?? 0) * 1e7),
      elevMilli: Math.round((step.el ?? 0) * 1000) + STOP_CAM_LIFT_MM,
      rangeMilli: 3500,
      pitchMilli: 0,
    });
  };

  // Present a comprehension check and GATE the play loop until it resolves — the
  // web preview of the Unreal hold-until-answered behaviour. Graded in the
  // browser by `QuizPanel` (no DB writes); resolves with correct/incorrect. Stop
  // resolves it early (correct=false) so the awaiting loop unblocks then bails.
  const presentCheck = (check: EditorCheck) => new Promise<boolean>((resolve) => {
    playRef.current.checkResolve = resolve;
    setActiveCheck(check);
  });
  const resolveActiveCheck = (correct: boolean) => {
    const r = playRef.current.checkResolve;
    playRef.current.checkResolve = null;
    setActiveCheck(null);
    // A standalone check preview (not a play-loop gate) has no awaiting resolver —
    // clear its running key so its "Stop Preview" button reverts to "Preview".
    if (!r) setPreviewingKey(null);
    r?.(correct);
  };

  // A World-selection check is answered in the scene: when one is presented, hand
  // the trainee free-fly control so they can navigate to the target twin, and
  // start from a clean selection so the prompt reads "click a twin" not a stale
  // pick. (MCQ checks leave the camera as the play loop left it.)
  //
  // #228: the cleanup hands the camera BACK when the check closes (answered,
  // revealed or timed out). Without it the camera stayed in freeLook, the next
  // stop gate's "Fly to marker" (an orbit-rig flyToGeo) was swallowed, the
  // marker was never reached, and Advance stayed disabled until a reload.
  useEffect(() => {
    if (!activeCheck || !isWorldSelection(activeCheck)) return;
    const v = viewerRef.current;
    if (v) { v.setCameraMode('freeLook'); v.setSelection([]); }
    setSelected3dTwin(null);
    return () => {
      const v2 = viewerRef.current;
      if (v2) { v2.setCameraMode('freeOrbit'); v2.setSelection([]); }
      setSelected3dTwin(null);
    };
  }, [activeCheck]);

  const stopPlay = () => {
    const s = playRef.current;
    s.cancelled = true;
    if (s.wake) s.wake();
    else if (s.timer) { clearTimeout(s.timer); s.timer = null; }
    if (s.fadeTimer) { clearTimeout(s.fadeTimer); s.fadeTimer = null; }
    if (s.hlTimer) { clearTimeout(s.hlTimer); s.hlTimer = null; }
    if (s.relTimer) { clearTimeout(s.relTimer); s.relTimer = null; }
    if (s.twinInfoTimer) { clearTimeout(s.twinInfoTimer); s.twinInfoTimer = null; }
    if (s.checkResolve) resolveActiveCheck(false); // unblock a gated check
    // Unblock a stop gate the loop is awaiting so it can bail, and clear its marker.
    if (stopConfirmRef.current) { const r = stopConfirmRef.current; stopConfirmRef.current = null; r(); }
    setStopGate(null);
    const v = viewerRef.current;
    if (v) { v.stopCamera(); v.setCameraMode(cameraMode); v.highlightTwin(''); v.highlightGeo(null); v.highlightFly(null); v.setTwinTransparency([], 1000); v.setRelationshipLinesVisible(false); v.showStopMarker(null); v.showTwinInfo(null); v.setPlaybackSpeed(1000); }
    setPlaying(false);
    setPlayCaption(null);
    setPlayPos(null);
  };

  // `scopeStepIds` = play ONLY those stops (in order); undefined = the whole
  // orientation. "Preview stop" passes the single selected stop so an author can
  // rehearse one stop without sitting through the tour (#10).
  const playOrientation = async (scopeStepIds?: string[]) => {
    const v = viewerRef.current;
    if (!v) { toast({ title: 'Switch to 3D to play the orientation', kind: 'bad' }); return; }
    const ids = scopeStepIds ?? orderedStepIds;
    type PlayItem = { stop: string; stepId: string; beat: Beat; step: EditorStep; si: number; sn: number; bi: number; bn: number };
    const items: PlayItem[] = [];
    ids.forEach((id, si) => {
      const step = model.steps.get(id);
      if (!step) return;
      const beats = step.scripts[EDIT_VERSION] ?? [];
      beats.forEach((beat, bi) =>
        items.push({ stop: step.name, stepId: id, beat, step, si, sn: ids.length, bi, bn: beats.length }));
    });
    if (!items.length) {
      toast({
        title: scopeStepIds ? 'This stop has no authored beats to play' : 'This orientation has no authored beats to play',
        kind: 'bad',
      });
      return;
    }

    playRef.current = { cancelled: false, timer: null, wake: null, fadeTimer: null, hlTimer: null, relTimer: null, twinInfoTimer: null, checkResolve: null, reschedule: null, recam: null };
    setPlaying(true);
    v.highlightTwin(''); // fresh stage — clear any carried highlight from a prior run
    v.setTwinTransparency([], 1000); // and any leftover fade from a preview / prior run
    v.setRelationshipLinesVisible(false); // and any global relationship lines from a prior run
    v.showTwinInfo(null); // and any ShowTwinInfo panel from a prior run
    setFocusSuppressed(true); // beat verbs own the camera now, and it stays put when playback ends
    setSelAction(null);   // drop any selected action so the inspector doesn't fight playback
    expandTimeline();     // open the dock so the "now playing" beat highlight is visible
    v.setCameraMode('freeOrbit');
    // Play-speed scales camera motion HOST-SIDE (each verb's durationMillis is
    // divided by the speed in fireBeatCamera), so the viewer clock stays at 1x —
    // this works on any /bevy bundle, with no dependency on the setPlaybackSpeed
    // handler being deployed. Reset to 1x in case a prior run left it scaled.
    v.setPlaybackSpeed(1000);
    // The camera is positioned per-stop by the interactive gate below (the author
    // flies INTO each stop's tube marker), so there is no auto-fly at start.

    // Per-beat hold timing lives in playHoldMs (model.ts): watchable floor,
    // open-ended-Orbit hold, and the #77 hard ceiling so no beat — however
    // broken its authored duration — can hold the stage forever.
    const STOP_GAP = 500; // brief breath between stops

    // A continuous Orbit started in one beat keeps spinning across the later beats
    // that carry it (their Camera lane just reads "⟲ continues…") until a
    // StopCamera, a new camera move takes over, or the stop ends — so we must NOT
    // stop it after every beat, only at those boundaries.
    let orbitRunning = false;
    // Local-graded quiz outcomes across the run (no DB writes) — drives the
    // end-of-run results summary.
    setQuizResults(null);
    const outcomes: QuizOutcome[] = [];
    for (let i = 0; i < items.length && !playRef.current.cancelled; i++) {
      const it = items[i];
      // Interactive stop gate: at the FIRST beat of each stop, show the stop's
      // tube marker and hold until the author flies into it and confirms
      // Start/Advance — the Unreal walk-to-marker workflow. `stopConfirmRef` is
      // resolved by the confirm button (enabled only while inside the tube) or by
      // Stop (which cancels the run).
      if (i === 0 || items[i - 1].si !== it.si) {
        // A carried orbit must not keep spinning while the author walks to the
        // next stop.
        if (orbitRunning) { v.stopCamera(); orbitRunning = false; }
        v.showStopMarker({
          latE7: Math.round((it.step.lat ?? 0) * 1e7),
          lonE7: Math.round((it.step.lon ?? 0) * 1e7),
          elevMilli: Math.round((it.step.el ?? 0) * 1000),
        });
        setStopGate({ stepId: it.stepId, label: it.si === 0 ? 'Start' : 'Advance', reached: false });
        await awaitStopConfirm();
        v.showStopMarker(null);
        setStopGate(null);
        if (playRef.current.cancelled) break;
      }
      // End a carried orbit when the flight crosses into a new stop, or when this
      // beat has its OWN camera command that takes over (fly / look / stop / a new
      // orbit) — stop the spin first so the new move isn't fought by it.
      const beatHasCamera = it.beat.actions.some((a) => a.kind === 'command' && laneOf(a) === 'camera');
      // End a carried orbit only at a STOP BOUNDARY, and only when the incoming
      // beat has NO camera command of its own. A beat WITH a camera command
      // replaces the motion itself (a new orbit/follow overwrites it in the
      // viewer), so a `stopCamera` here is not just redundant — it races that
      // command in the viewer's single-frame inbound drain and CANCELS it
      // (`receive_camera_motion` applies stop AFTER orbit/follow → nets to Idle;
      // a fly-to's scripted flight is likewise cancelled). That was the
      // "follow/fly dead right after an Orbit" bug. (Fly/look after an orbit
      // still rely on the viewer dropping the stale motion so it doesn't fight
      // them — handled viewer-side.)
      if (orbitRunning && !beatHasCamera && i > 0 && items[i - 1].si !== it.si) {
        v.stopCamera();
        orbitRunning = false;
      }
      // Follow the flight in the editor: select the playing stop (its beats then
      // show in the Timeline) and record the active beat for the highlight. React
      // bails out of the setState when the value is unchanged, so re-selecting the
      // same stop each beat is a no-op; focusGeo is gated off while playing, so the
      // selection never triggers a competing focus fly.
      setSelectedStepId(it.stepId);
      setPlayPos({ stepId: it.stepId, bi: it.bi });
      // Caption: the beat's narration ONLY. A beat with no narration shows no
      // caption at all (the card is hidden below) — we deliberately do NOT fall
      // back to summarising the beat's commands, which read as if the commands
      // were being narrated.
      let narr = '';
      for (const a of it.beat.actions) if (a.kind === 'narrate' && a.text.trim()) { narr = a.text.trim(); break; }
      setPlayCaption({
        stop: it.stop,
        text: narr,
        progress: `Stop ${it.si + 1}/${it.sn} · beat ${it.bi + 1}/${it.bn}`,
      });

      // Effects lane: reflect a Highlight effect as the viewer's pulsing marker.
      // A HighlightTwin/HighlightGeo command lights the twin/point and — like the
      // runtime (UTwinHighlightSubsystem, 5 s default) — auto-clears after its
      // authored DURATION, so the cue ends instead of bleeding into later beats
      // (e.g. a fly-to's highlight into the next orbit beat). ClearSelection or a
      // new stop clears it immediately; carry beats leave it until its timer fires.
      {
        let hlCmd: CommandAction | null = null;
        for (const a of it.beat.actions) {
          if (a.kind === 'command' && (a.commandType === 'HighlightTwin' || a.commandType === 'HighlightGeo')) hlCmd = a;
        }
        const clearMarker = () => { viewerRef.current?.highlightTwin(''); viewerRef.current?.highlightGeo(null); viewerRef.current?.highlightFly(null); };
        // Beat-scoped: every beat cancels a pending auto-clear, then either
        // re-highlights (its own command) or clears — so a highlight never bleeds
        // past its own beat. A ClearSelection / plain / narration beat has no
        // highlight command, so it falls to the clear branch automatically.
        if (playRef.current.hlTimer) { clearTimeout(playRef.current.hlTimer); playRef.current.hlTimer = null; }
        if (hlCmd) {
          const twinId = hlCmd.twinIds[0];
          if (twinId) {
            // Highlight the twin at its ACTUAL viewer position (mesh centre) —
            // the viewer resolves it, so the marker lands on the twin, not on
            // its stored geo anchor.
            v.highlightTwin(twinId);
          } else {
            // Command carries no twin — fall back to the stop's coordinate.
            v.highlightGeo({
              latE7: Math.round((it.step.lat ?? 0) * 1e7),
              lonE7: Math.round((it.step.lon ?? 0) * 1e7),
              elevMilli: Math.round((it.step.el ?? 0) * 1000),
            });
          }
          // Auto-clear after the authored duration (runtime default 5000 ms) —
          // a floor within the beat; the next beat clears it regardless.
          const durMs = hlCmd.durationMs ?? 5000;
          playRef.current.hlTimer = setTimeout(() => { playRef.current.hlTimer = null; clearMarker(); }, durMs);
        } else {
          clearMarker();
        }
      }

      // Effects lane: Transparency. A SetTransparency beat fades its twin(s) via
      // the viewer's setTwinTransparency verb (replace-set — the latest fade
      // wins); opacity >= 1 restores. Effects are BEAT-SCOPED: a fade lasts only
      // its own beat — any following beat that does NOT itself set a fade clears
      // it, so an effect stops automatically when its beat is done (it no longer
      // bleeds across beats within a stop). An authored duration can still end it
      // early within the beat.
      {
        const trCmd = it.beat.actions.find(
          (a): a is CommandAction => a.kind === 'command' && a.commandType === 'SetTransparency',
        ) ?? null;
        // Any change to the fade makes a pending auto-revert stale — cancel it.
        if (playRef.current.fadeTimer) { clearTimeout(playRef.current.fadeTimer); playRef.current.fadeTimer = null; }
        if (trCmd) {
          const opacity = trCmd.opacity ?? 0.25;
          const opacityMilli = Math.min(1000, Math.max(0, Math.round(opacity * 1000)));
          v.setTwinTransparency(trCmd.twinIds, opacityMilli, trCmd.dither);
          if (trCmd.durationMs != null && opacityMilli < 1000) {
            playRef.current.fadeTimer = setTimeout(() => {
              playRef.current.fadeTimer = null;
              viewerRef.current?.setTwinTransparency([], 1000);
            }, trCmd.durationMs);
          }
        } else {
          // No fade on this beat → clear any fade the previous beat left running.
          v.setTwinTransparency([], 1000);
        }
      }

      // Effects lane: relationship lines. ShowRelationships / HideRelationships
      // toggle the viewer's relationship rendering and PERSIST across beats
      // (unlike the beat-scoped Highlight / Transparency above) until a later
      // HideRelationships — matching the runtime, where show and hide are
      // separate commands. An empty twin list drives the global show-all toggle;
      // otherwise toggle each listed twin's lines. Needs a /bevy carrying the
      // relationship verbs; an older one drops them silently.
      if (playRef.current.relTimer) { clearTimeout(playRef.current.relTimer); playRef.current.relTimer = null; }
      for (const a of it.beat.actions) {
        if (a.kind !== 'command') continue;
        if (a.commandType === 'ShowRelationships' || a.commandType === 'HideRelationships') {
          const shown = a.commandType === 'ShowRelationships';
          const toggle = (s: boolean) => {
            if (a.twinIds.length === 0) viewerRef.current?.setRelationshipLinesVisible(s);
            else for (const id of a.twinIds) viewerRef.current?.setRelationships(id, s);
          };
          toggle(shown);
          // A ShowRelationships auto-hides after its duration (like a timed
          // Transparency). The duration field always shows a value (default 2 s),
          // so honour the SHOWN value — `?? 2000` — rather than only an explicitly
          // edited one, or the displayed "2 s" would never take effect.
          // HideRelationships is instantaneous (no duration field).
          if (shown) {
            playRef.current.relTimer = setTimeout(() => { playRef.current.relTimer = null; toggle(false); }, a.durationMs ?? 2000);
          }
        }
      }

      // Effects lane: ShowTwinInfo panel. Beat-scoped like Transparency: the
      // panel shows for its beat; any following beat that does NOT set one
      // clears it. The viewer resolves the chosen property/telemetry values +
      // the twin's screen position; ViewerPane draws the card. Needs a /bevy
      // carrying the showTwinInfo verb; an older one drops it silently.
      if (playRef.current.twinInfoTimer) { clearTimeout(playRef.current.twinInfoTimer); playRef.current.twinInfoTimer = null; }
      {
        const tiCmd = it.beat.actions.find(
          (a): a is CommandAction => a.kind === 'command' && a.commandType === 'ShowTwinInfo',
        ) ?? null;
        if (tiCmd && tiCmd.twinIds[0]) {
          v.showTwinInfo({
            twinId: tiCmd.twinIds[0],
            propertyNames: tiCmd.propertyNames,
            telemetryNames: tiCmd.telemetryNames,
          });
          if (tiCmd.durationMs != null) {
            playRef.current.twinInfoTimer = setTimeout(() => {
              playRef.current.twinInfoTimer = null;
              viewerRef.current?.showTwinInfo(null);
            }, tiCmd.durationMs);
          }
        } else {
          // No panel on this beat → clear one a previous beat left showing.
          v.showTwinInfo(null);
        }
      }

      // Effects lane: camera navigation mode. Apply the authored mode so Play
      // reflects the switch (freeFly/orbit/walk -> freeLook/freeOrbit/walk). It
      // persists until another SetCameraMode or a camera move re-asserts the rig.
      // Placed before fireBeatCamera: a pure mode-switch beat has no camera-lane
      // command, so fireBeatCamera returns early and never overrides it.
      {
        const cmCmd = it.beat.actions.find(
          (a): a is CommandAction => a.kind === 'command' && a.commandType === 'SetCameraMode',
        ) ?? null;
        if (cmCmd) {
          const mode = cmCmd.message === 'walk' ? 'walk' : cmCmd.message === 'orbit' ? 'freeOrbit' : 'freeLook';
          v.setCameraMode(mode);
        }
      }

      const fired = fireBeatCamera(v, it.beat, it.step);
      // A skipped camera move (#77) fails loudly instead of silently holding a
      // flight that never happens; the beat still plays its narration/effects
      // and advances on its own bounded timing.
      if (fired.skipped) {
        toast({
          title: `Camera move skipped — ${it.stop}, beat ${it.bi + 1}`,
          sub: fired.skipped,
          kind: 'bad',
        });
      }
      // Track the orbit across carry beats: a beat with its OWN camera command
      // resets the running state (a new continuous keeps spinning; a fly/look/stop
      // takes over and ends it); a beat with no camera command leaves it as-is.
      if (fired.hadCamera) orbitRunning = fired.continuous;
      const driver = beatDriver(it.beat).ms; // narration / wait / authored duration
      const hold = playHoldMs(driver, fired.ms, orbitRunning);
      await holdMs(hold);
      if (playRef.current.cancelled) break;
      // The beat has finished presenting: fire its comprehension checks and HOLD
      // until each is answered (graded in the browser, nothing written). Mirrors
      // the Unreal trainee gate — one check at a time, in authored order.
      for (const check of checksForBeat(it.step.checks, it.bi)) {
        const correct = await presentCheck(check);
        if (playRef.current.cancelled) break;
        outcomes.push({ check, correct });
      }
      if (playRef.current.cancelled) break;
      if (i + 1 < items.length && items[i + 1].si !== it.si) await holdMs(STOP_GAP);
    }

    if (playRef.current.fadeTimer) { clearTimeout(playRef.current.fadeTimer); playRef.current.fadeTimer = null; }
    if (playRef.current.hlTimer) { clearTimeout(playRef.current.hlTimer); playRef.current.hlTimer = null; }
    if (playRef.current.twinInfoTimer) { clearTimeout(playRef.current.twinInfoTimer); playRef.current.twinInfoTimer = null; }
    const done = viewerRef.current;
    if (done) { done.stopCamera(); done.setCameraMode(cameraMode); done.highlightTwin(''); done.highlightGeo(null); done.highlightFly(null); done.setTwinTransparency([], 1000); done.showStopMarker(null); done.showTwinInfo(null); done.setPlaybackSpeed(1000); }
    setStopGate(null);
    setPlaying(false);
    setPlayCaption(null);
    setPlayPos(null);
    // Finished (not stopped) with checks answered → show the results summary.
    if (!playRef.current.cancelled && outcomes.length > 0) setQuizResults(outcomes);
  };

  // Stop playback the instant the author leaves 3D (the viewer unmounts) or the
  // component tears down — a scripted flight must never outlive its stage.
  useEffect(() => {
    if (viewMode !== '3d' && playing) stopPlay();
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    playRef.current.cancelled = true;
    if (playRef.current.timer) clearTimeout(playRef.current.timer);
    if (playRef.current.fadeTimer) clearTimeout(playRef.current.fadeTimer);
    if (playRef.current.hlTimer) clearTimeout(playRef.current.hlTimer);
    if (playRef.current.twinInfoTimer) clearTimeout(playRef.current.twinInfoTimer);
  }, []);
  // Mirror the Play-speed into the ref (read by the loop's holds + fireBeatCamera's
  // sentDur) and, while a run is live, re-pace the CURRENT beat on the spot: re-time
  // its hold (reschedule) AND re-fire the in-flight fly/look move at the new pace
  // (recam). Camera motion scales host-side per verb, so there's no viewer clock to
  // push; flyAlongPath / continuous Orbit-Follow have no recam and just re-time the hold.
  useEffect(() => {
    playSpeedRef.current = playSpeed;
    if (playing) { playRef.current.reschedule?.(); playRef.current.recam?.(); }
  }, [playSpeed, playing]);

  // Commit the draft to the module. Called by the debounced auto-apply below
  // (edits sync automatically, no Save button), by Ctrl+S as a manual flush,
  // and by guardDirty when switching stops mid-debounce. Shared-step edits
  // (usedIn > 1) apply to every orientation: the shared badge in the header is
  // the warning; a confirm here would fire mid-typing under auto-apply.
  // A check the module would reject (no correct option, several on a
  // single-select, or a beat with no actions) blocks the WHOLE stop save rather
  // than being dropped silently: the draft stays dirty, so fixing it commits
  // everything, and guardDirty keeps the author on the stop meanwhile. The
  // Inspector shows the reason on the check itself; this ref keeps the auto-
  // apply debounce from re-toasting the same message on every keystroke.
  const toastedCheckError = useRef<string | null>(null);

  /** Returns false when the save was REFUSED client-side (an invalid check), so
   *  a caller flushing before a navigation can hold the author on the stop. */
  function saveStop(): boolean {
    const d = draft;
    if (!d) return true;
    const invalid = beatsError(d.scripts[EDIT_VERSION] ?? []) ?? checksError(d.checks, authoredBeats(d), knownTwinIds);
    if (invalid) {
      if (toastedCheckError.current !== invalid) {
        toastedCheckError.current = invalid;
        toast({ title: 'Save stop failed', sub: invalid, kind: 'bad' });
      }
      return false;
    }
    toastedCheckError.current = null;
    withConn((conn) => {
      // Every write is collected and returned, so withConn surfaces a refusal of
      // ANY of them (only setStepChecks used to be returned; the rest failed
      // silently while the dirty dot cleared).
      const writes: Promise<unknown>[] = [];
      writes.push(conn.reducers.updateOrientationStep({
        stepId: d.stepId,
        displayName: d.name,
        location: { latitude: d.lat, longitude: d.lon, elevation: d.el },
        durationMinutes: d.durationMinutes,
      }));
      // Single-length mode: the ONLY slot this editor writes is EDIT_VERSION.
      // Legacy timed slots ride along in the draft but are never written back,
      // so they stay exactly as the module has them.
      const vb = d.scripts[EDIT_VERSION];
      const plan = scriptWrite(vb, actionsReady);
      if (vb && plan === 'write') {
        writes.push(conn.reducers.setOrientationStepScript({
          stepId: d.stepId,
          orientationVersion: DEFAULT_VERSION,
          source: AUTHORED_SOURCE,
          totalDurationEstimateMs: estimateMs(vb) || undefined,
        }));
        writes.push(conn.reducers.setScriptActions({
          stepId: d.stepId,
          orientationVersion: DEFAULT_VERSION,
          entries: beatsToEntries(vb),
        }));
      } else if (plan === 'clear') {
        // The author deleted every beat. The slot exists only once actions were
        // read (model.ts builds it from action rows), so an empty array here is
        // a deletion, not a not-yet-loaded stop; set_script_actions with an
        // empty list clears the step's actions. Before this, the guard above
        // skipped the write and the old beats stayed on the server.
        writes.push(conn.reducers.setScriptActions({
          stepId: d.stepId,
          orientationVersion: DEFAULT_VERSION,
          entries: [],
        }));
      }
      setDirty(false);
      // Bulk replace (an empty list clears the step's checks), so deleting the
      // last check persists; checks hang off the step, not off a script slot,
      // so it runs even for a stop with no authored beats. Only for a draft
      // whose checks were seeded from a loaded cache (see checksSeededRef):
      // never write a list we may simply not have read yet. Returned, not
      // fired: withConn can only surface a rejection it is handed the promise
      // for, and an un-awaited refusal here is silent data loss.
      if (checksSeededRef.current === d.stepId) {
        writes.push(conn.reducers.setStepChecks({ stepId: d.stepId, entries: checksToEntries(d.checks) }));
      }
      return Promise.all(writes);
    }, 'Save stop');
    return true;
  }

  // Auto-apply (miro-style): every draft edit commits on its own after a
  // short debounce — each keystroke re-arms the timer, so a typing burst
  // commits once. The dirty dot doubles as the transient "syncing" cue.
  useEffect(() => {
    if (!dirty || !draft) return;
    const t = setTimeout(saveStop, 1200);
    return () => clearTimeout(t);
  }, [dirty, draft]);

  // Ctrl/Cmd+S saves the dirty draft instead of the browser's save dialog.
  // Ref indirection so the one window listener always sees the latest closure.
  const saveKeyRef = useRef<() => void>(() => {});
  saveKeyRef.current = () => { if (dirty && draft) saveStop(); };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveKeyRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- structural ops (live) --------------------------------------------
  // Add-stop is a two-step gesture: "+ Add stop" arms placement mode, then the
  // next map click creates the stop at the chosen location (placeNewStop).
  function toggleAddStop() {
    if (!orientationId) return;
    setPickTarget(null);
    setPlacingStop((p) => !p);
  }

  // Create a new stop at a placed location. `el` is a real elevation: a raycast
  // from the 3D pick-point path, or the terrarium-DEM sample the 2D map resolves
  // in `placeStopFrom2D`. It is only absent when the DEM lookup fails, in which
  // case we inherit the selected/first stop's elevation (500 for the very
  // first one) exactly as before.
  function placeNewStop(lat: number, lon: number, el?: number) {
    if (!orientationId) return;
    placedRef.current = { lat, lon };
    let elevation: number;
    if (el != null) {
      elevation = el;
    } else {
      const seed = selStep ?? (orderedStepIds[0] ? stepOf(orderedStepIds[0]) : null);
      elevation = seed ? seed.el : 500;
    }
    const stepId = uid('step');
    withConn((conn) => {
      conn.reducers.createOrientationStep({ stepId, projectId, displayName: 'New stop', location: { latitude: lat, longitude: lon, elevation }, durationMinutes: estimateDwellMinutes({ scripts: {} }) });
      conn.reducers.addStepToOrientation({ orientationId, stepId, position: (orderedStepIds.length + 1) * 100 });
      setSelectedStepId(stepId);
      setSelAction(null);
      setPlacingStop(false);
      toast({
        title: 'Stop added',
        sub: el != null ? `Elevation ${fmtElevation(el)}` : 'Placed on the map',
        kind: 'good',
      });
    }, 'Add stop');
  }

  // Reuse an existing step: link it into this orientation WITHOUT creating a new
  // one. The step keeps its shared fields + scripts (see saveStop's warning).
  function addExistingStop(stepId: string) {
    if (!orientationId) return;
    withConn((conn) => {
      conn.reducers.addStepToOrientation({ orientationId, stepId, position: (orderedStepIds.length + 1) * 100 });
      setSelectedStepId(stepId);
      setSelAction(null);
      setModal(null);
      toast({ title: 'Existing stop added', kind: 'good' });
    }, 'Add existing stop');
  }

  // Copy a stop (fields, scripts, checks) under a new name so it can be edited
  // without touching the original. `replaceHere` swaps the copy in for the
  // original in this orientation (the "make this shared stop mine" case);
  // otherwise the copy is appended (picker, or an unshared stop).
  async function copyStop(stepId: string, replaceHere: boolean) {
    if (!orientationId) return;
    // The copy is made from the module's rows, and selection then moves to it,
    // so flush pending edits first.
    if (!guardDirty()) return;
    const name = await dialogs.prompt({
      title: 'Copy stop',
      label: 'Name',
      initial: `${model.steps.get(stepId)?.name ?? 'stop'}_copy`,
      body: replaceHere
        ? 'The copy replaces the shared stop in this orientation only. The other orientations keep the original.'
        : 'The copy is added to this orientation. Edits to it never affect the original.',
      confirmLabel: 'Copy',
    });
    if (!name?.trim()) return;
    const newStepId = uid('step');
    withConn((conn) =>
      conn.reducers.copyOrientationStep({
        sourceStepId: stepId,
        newStepId,
        displayName: name.trim(),
        orientationId: replaceHere ? orientationId : undefined,
      })
        .then(() => replaceHere
          ? undefined
          : conn.reducers.addStepToOrientation({ orientationId, stepId: newStepId, position: (orderedStepIds.length + 1) * 100 }))
        .then(() => {
          setSelectedStepId(newStepId);
          setSelAction(null);
          setModal(null);
          toast({ title: 'Stop copied', sub: name.trim(), kind: 'good' });
        }), 'Copy stop');
  }

  // Delete an orphan step (usage 0) straight from the picker. It is already
  // unlinked, so no remove step is needed before the guarded delete.
  async function deleteOrphanStep(stepId: string) {
    const name = model.steps.get(stepId)?.name ?? 'stop';
    if (
      !(await dialogs.confirm({
        title: `Delete unused stop "${name}"?`,
        body: 'The stop is deleted permanently. This cannot be undone.',
        confirmLabel: 'Delete stop',
        danger: true,
      }))
    )
      return;
    withConn((conn) => {
      conn.reducers.deleteOrientationStep({ stepId });
      toast({ title: 'Stop deleted', sub: name, kind: 'good' });
    }, 'Delete stop');
  }

  // `id` defaults to the selected stop; a stops-list row's trash button passes
  // its own, and is selected first so the remove dialog shows the right stop.
  function removeStop(id = selectedStepId) {
    if (!id || !orientationId) return;
    if (id !== selectedStepId) {
      if (!guardDirty()) return;
      setSelectedStepId(id);
      setSelAction(null);
    }
    const usage = model.usage.get(id) ?? 0;
    // Used elsewhere: unlinking here is safe and never orphans the step.
    if (usage > 1) {
      const others = usage - 1;
      withConn((conn) => {
        conn.reducers.removeStepFromOrientation({ orientationId, stepId: id });
        setSelectedStepId(null);
        setSelAction(null);
        toast({ title: 'Removed from this orientation', sub: `Still used in ${others} other${others === 1 ? '' : 's'}`, kind: 'good' });
      }, 'Remove stop');
      return;
    }
    // Last/only user: ask whether to delete the step or keep it for reuse.
    setModal('remove');
  }

  // "Delete permanently" from the remove dialog: unlink first (the delete reducer
  // refuses while the step is still linked), then cascade-delete.
  function confirmDeleteStop() {
    if (!selectedStepId || !orientationId) return;
    const id = selectedStepId;
    const name = model.steps.get(id)?.name ?? 'stop';
    withConn((conn) => {
      conn.reducers.removeStepFromOrientation({ orientationId, stepId: id });
      conn.reducers.deleteOrientationStep({ stepId: id });
      setSelectedStepId(null);
      setSelAction(null);
      setModal(null);
      toast({ title: 'Stop deleted', sub: name, kind: 'good' });
    }, 'Delete stop');
  }

  // "Keep for future use" from the remove dialog: unlink only; the step stays in
  // the pool and is reusable via "Add existing".
  function confirmKeepStop() {
    if (!selectedStepId || !orientationId) return;
    const id = selectedStepId;
    withConn((conn) => {
      conn.reducers.removeStepFromOrientation({ orientationId, stepId: id });
      setSelectedStepId(null);
      setSelAction(null);
      setModal(null);
      toast({ title: 'Removed; kept for reuse', kind: 'good' });
    }, 'Remove stop');
  }

  function reorder(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= orderedStepIds.length) return;
    const arr = [...orderedStepIds];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    withConn((conn) => conn.reducers.reorderSteps({ orientationId, orderedStepIds: arr }), 'Reorder');
  }

  /** Drag-reorder from the stops list: move `from` to gap `insert` (live). */
  function reorderTo(from: number, insert: number) {
    if (isNoopMove(from, insert)) return;
    const arr = moveItem(orderedStepIds, from, insert);
    withConn((conn) => conn.reducers.reorderSteps({ orientationId, orderedStepIds: arr }), 'Reorder');
  }

  // Save flows live in orientationSave.ts and are AWAITED end-to-end (#76):
  // reducer promises reject with the module's refusal (e.g. require_site_admin's
  // "Not an admin of this site" on a site the caller has no user_site row for),
  // and the old fire-and-forget path toasted success anyway — authors lost work
  // behind a lying "Orientation created (Draft)" on ludvig-testing-hermosa.
  // Success effects (picker selection, closing the modal, the toast) now run
  // only after the module commits; a refusal keeps the modal open and withConn
  // toasts the server's actual error.
  function createOrientation(d: OrientationDraft) {
    if (!projectId) { toast({ title: 'Select a site first', kind: 'bad' }); return; }
    const id = uid('orient');
    withConn((conn) =>
      runCreateOrientation(conn.reducers, id, projectId, d, DEFAULT_VERSION).then(() => {
        setCurrentId(id);
        setModal(null);
        toast({ title: 'Orientation created (Draft)', sub: d.displayName, kind: 'good' });
      }), 'Create orientation');
  }

  // Duplicate the current orientation as a Draft linking the same (shared)
  // stops; the author then copies only the stops that should differ.
  async function duplicateCurrentOrientation() {
    if (!currentOrientation) return;
    const name = await dialogs.prompt({
      title: 'Duplicate orientation',
      label: 'Name',
      initial: `${currentOrientation.displayName}_copy`,
      body: 'The copy starts as a Draft and uses the same stops, which stay shared. Use Copy on a stop to change it in the copy only.',
      confirmLabel: 'Duplicate',
    });
    if (!name?.trim()) return;
    const id = uid('orient');
    withConn((conn) =>
      conn.reducers.duplicateOrientation({ sourceOrientationId: orientationId, newOrientationId: id, displayName: name.trim() })
        .then(() => {
          setCurrentId(id);
          setSelectedStepId(null);
          setSelAction(null);
          toast({ title: 'Orientation duplicated (Draft)', sub: name.trim(), kind: 'good' });
        }), 'Duplicate orientation');
  }

  function saveDetails(d: OrientationDraft) {
    if (!currentOrientation) return;
    withConn((conn) =>
      runSaveDetails(conn.reducers, orientationId, d, currentOrientation.status, DEFAULT_VERSION).then(() => {
        setModal(null);
        toast({ title: 'Orientation updated', kind: 'good' });
      }), 'Update orientation');
  }

  /** Delete the current orientation. UI-exposed for Draft and Archived; the
   *  reducer also refuses if the orientation has active assignments. */
  async function deleteCurrentOrientation() {
    if (!currentOrientation || (currentOrientation.status !== 'Draft' && currentOrientation.status !== 'Archived')) return;
    if (
      !(await dialogs.confirm({
        title: `Delete ${currentOrientation.status.toLowerCase()} orientation "${currentOrientation.displayName}"?`,
        body: 'This cannot be undone.',
        confirmLabel: 'Delete orientation',
        danger: true,
      }))
    )
      return;
    const id = orientationId;
    const name = currentOrientation.displayName;
    withConn((conn) => conn.reducers.deleteOrientation({ orientationId: id }).then(() => {
      setCurrentId('');
      setSelectedStepId(null);
      setSelAction(null);
      toast({ title: 'Orientation deleted', sub: name, kind: 'good' });
    }), 'Delete orientation');
  }

  async function setStatus(status: EditorOrientation['status']) {
    // Publishing with no authored scripts is allowed but easy to do by mistake.
    if (status === 'Active' && currentOrientation?.status === 'Draft' && !hasAnyScript) {
      if (
        !(await dialogs.confirm({
          title: 'Publish with no scripts?',
          body: 'No stop has an authored script yet.',
          confirmLabel: 'Publish anyway',
        }))
      )
        return;
    }
    // Awaited like the saves above (#76): publish/archive must not toast
    // success while the module refuses the write.
    withConn((conn) =>
      conn.reducers.setOrientationStatus({ orientationId, status: { tag: status } }).then(() =>
        toast({ title: `Orientation ${status === 'Active' ? 'published' : status.toLowerCase()}`, kind: 'good' }),
      ), 'Set status');
  }

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Orientation Editor" />;
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Orientation Editor" siteName={currentSite.name} />;

  // Null until the canvas has been measured on first run (one frame).
  const stopsShell = floatShell('stops');
  const inspectorShell = floatShell('inspector');

  const statusActions = currentOrientation
    ? currentOrientation.status === 'Draft'
      ? [{ label: 'Publish', to: 'Active' as const, hint: 'Make this orientation live: visible in pickers, assignable, and eligible for auto-assign' }]
      : currentOrientation.status === 'Active'
        ? [{ label: 'Archive', to: 'Archived' as const, hint: 'Soft-remove: hidden from pickers and no longer assignable; existing assignments are kept' }]
        : [{ label: 'Restore', to: 'Active' as const, hint: 'Make this archived orientation Active again' }]
    : [];

  return (
    <div className={'oe-root' + (playing ? ' playing' : '')}>
      <header className="oe-head">
        <strong className="oe-title">Orientation Editor</strong>
        <SearchSelect
          variant="select"
          value={orientationId}
          disabled={!projectOrientations.length}
          placeholder={!modelReady ? 'Loading…' : projectOrientations.length === 0 ? 'No orientations on this site' : 'Search orientations…'}
          options={listedOrientations.map((o) => ({
            value: o.orientationId,
            label: o.displayName,
            badge: o.status,
          }))}
          onChange={(v) => { if (guardDirty()) setCurrentId(v); }}
        />
        {archivedCount > 0 && (
          <label className="oe-check" title="List archived orientations in the picker">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            archived ({archivedCount})
          </label>
        )}
        {projectOrientations.length > 0 && (
          <select
            className="oe-select sm"
            value={authorFilter}
            onChange={(e) => setAuthorFilter(e.target.value)}
            title="Only list orientations created by this author"
          >
            <option value="">All authors</option>
            {myUserId && <option value={myUserId}>Mine</option>}
            {authors.map((id) => <option key={id} value={id}>{authorName(id)}</option>)}
          </select>
        )}
        {currentOrientation && (
          <span
            className={'oe-status ' + currentOrientation.status.toLowerCase()}
            title={STATUS_DESC[currentOrientation.status]}
          >{currentOrientation.status}</span>
        )}

        {/* The Length switcher is gone: single-length mode authors one script
            per orientation (model.ts EDIT_VERSION). */}

        <span className="oe-spacer" />
        <GatedButton
          className="oe-btn sm"
          onClick={() => setModal('new')}
          reason={!hasSite ? 'Select a site first' : !minetwinOnline ? 'Reconnecting to MineTwin…' : null}
          hint="Create a new orientation"
        >+ New</GatedButton>
        <GatedButton
          className="oe-btn sm"
          onClick={() => setModal('details')}
          reason={currentOrientation ? null : 'Pick an orientation first'}
          hint="Edit this orientation's name, description, occupation targeting, auto-assign and status"
        >Details</GatedButton>
        <GatedButton
          className="oe-btn sm"
          onClick={duplicateCurrentOrientation}
          reason={currentOrientation ? null : 'Pick an orientation first'}
          hint="Create a Draft copy of this orientation with the same stops"
        >Duplicate</GatedButton>
        {statusActions.map((a) => (
          <button key={a.label} className="oe-btn primary sm" onClick={() => setStatus(a.to)} title={a.hint}>{a.label}</button>
        ))}
        {(currentOrientation?.status === 'Draft' || currentOrientation?.status === 'Archived') && (
          <button
            className="oe-btn danger sm"
            onClick={deleteCurrentOrientation}
            title={`Delete this ${currentOrientation.status.toLowerCase()} orientation`}
          >Delete</button>
        )}
        <button
          className="oe-btn ghost sm"
          onClick={resetLayout}
          title="Reset panel positions and sizes to their defaults"
        >Reset layout</button>
      </header>

      {projectOrientations.length === 0 ? (
        <div className="oe-panel" style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <div className="oe-empty">
            {hasSite && !modelReady ? (
              <>
                <h4>{minetwinOnline ? 'Loading orientations…' : 'Reconnecting to MineTwin…'}</h4>
                {minetwinOnline
                  ? `Fetching ${currentSite?.name ?? 'this site'}’s orientations.`
                  : 'The connection dropped. Your orientations are safe and will reappear when it is back.'}
              </>
            ) : hasSite ? (
              <>
                <h4>No orientations on {currentSite?.name ?? 'this site'} yet</h4>
                Create one to start placing stops and authoring scripts.
                <div style={{ marginTop: 12 }}>
                  <GatedButton className="oe-btn primary" onClick={() => setModal('new')} reason={minetwinOnline ? null : 'Reconnecting to MineTwin…'}>+ New orientation</GatedButton>
                </div>
              </>
            ) : (
              <>
                <h4>No site selected</h4>
                Select a site from the top bar to author its orientations.
              </>
            )}
          </div>
        </div>
      ) : (
      <div className={'oe-canvas' + (placingStop || pickTarget ? ' placing' : '')} ref={canvasRef} style={dockStyle}>
        {/* The map IS the workspace: it fills the canvas edge to edge. */}
        <div className="oe-canvas-map">
          {viewMode === '2d' ? (
            <StepMap
              stops={mapStops}
              selectedStepId={selectedStepId}
              onSelect={selectStop}
              onPlace={placingStop ? placeStopFrom2D : pickTarget ? pickLocationFrom2D : undefined}
              onMove={!placingStop && !pickTarget && selStep ? (_id, lat, lon) => moveStopFrom2D(lat, lon) : undefined}
              placing={placingStop || pickTarget !== null}
              twins={showTwinMarkers ? twinMarkers : undefined}
              movers={showTwinMarkers ? truckMovers : undefined}
              geo={showTwinMarkers ? geoGroups : undefined}
              commandGeo={cmdGeo}
              basemap={basemap}
            />
          ) : (
            // 3D pane: see the site, fly to the selected stop, and — when
            // placement is armed — click the ground to add a stop WITH its real
            // elevation (pick-point tool). Elevation is what 2D placement can't
            // give.
            <>
              {hasSite && (
                <div className="oe-fly-hint">
                  {cameraMode === 'freeLook'
                    ? 'Free Look · WASD move · hold RMB to look · Q/E down·up · wheel speed'
                    : cameraMode === 'walk'
                    ? 'Walk · click to look (Esc frees mouse) · WASD walk · Space jump · Shift run'
                    : 'Free Orbit · hold RMB to orbit · MMB pan · wheel zoom'}
                </div>
              )}
              {hasSite && selected3dTwin && twinOptions.includes(selected3dTwin) && (
                <div className="oe-target-hint">
                  <Icon of={Icons.content.twin} size={12} /> Selected twin:{' '}
                  <strong title={selected3dTwin}>{twinNames.get(selected3dTwin) ?? selected3dTwin}</strong>
                </div>
              )}
              {hasSite ? (
                <ViewerPane
                  ref={viewerRef}
                  projectSlug={projectId}
                  focusGeo={focusGeo}
                  ghostMarkersCsv={stopsCsv}
                  pickMode={placingStop || pickTarget !== null}
                  onPointPicked={(lat, lon, el) =>
                    placingStop ? placeNewStop(lat, lon, el) : pickLocation(lat, lon, el)}
                  onSelected={setSelected3dTwin}
                  onStopMarkerReached={onStopMarkerReached}
                  onStopMarkerLeft={onStopMarkerLeft}
                  onTwinDoubleClicked={(twinId) => {
                    // Double-clicking a twin in the 3D scene adds it to the
                    // selected World-selection check's expected set. Ignored for
                    // any other check type / when no check is selected (the
                    // viewer still frames the twin as usual).
                    const c = selectedCheck;
                    if (c && isWorldSelection(c)) {
                      onCheckChange(addWorldSelectionTwin(c, twinId, twinNames.get(twinId) ?? twinId));
                    }
                  }}
                  cameraMode={cameraMode}
                  cameraParams={cameraParams}
                  twinMarkersVisible={showTwinMarkers}
                  units={units}
                  highlightGeo={playing ? undefined : highlightGeo}
                  highlightFly={playing ? undefined : highlightFly}
                  highlightTwin={playing ? undefined : (cmdTwin ?? '')}
                  pathOverlay={playing ? undefined : pathOverlay}
                  onProtocolVersion={setViewerLevel}
                  onMeasureChanged={setMeasureMsg}
                  onCameraParamChanged={(mode, freeLookSpeedMm) => {
                    // Shift+wheel / +/- in the viewer tuned the Free Look
                    // speed: mirror it into the Camera Controls slider (and
                    // localStorage). Our re-send of the same value is a no-op.
                    if (mode !== 'freeLook') return;
                    onCameraValues({
                      ...cameraValues,
                      freeLook: { ...cameraValues.freeLook, moveSpeed: freeLookSpeedMm / 1000 },
                    });
                  }}
                />
              ) : (
                <div className="oe-viewer-overlay">Select a site to view it in 3D.</div>
              )}
            </>
          )}
        </div>

        {/* Controls for both panes, floated top-center above the map layer
            (sibling of .oe-canvas-map so Leaflet's isolated z-stack can't
            swallow it, below the floating cards). */}
        <div className="oe-top-controls">
          {viewMode === '2d' ? (
            <LayersMenu value={basemap} onChange={setBasemap} />
          ) : (
            hasSite && (
              <CameraControl
                mode={cameraMode}
                values={cameraValues}
                onModeChange={setCameraMode}
                onValuesChange={onCameraValues}
              />
            )
          )}
          {/* Preview group: scope (whole orientation / selected stop), one
              play-or-stop button, play speed. The scope is sticky so iterating
              on a single stop is one click; it locks while a run is live. */}
          {viewMode === '3d' && hasSite && (
            <div className="oe-seg oe-preview-group" role="group" aria-label="Preview">
              <span className="oe-preview-label">Preview</span>
              <select
                value={previewScope}
                disabled={playing}
                onChange={(e) => setPreviewScope(e.target.value as PreviewScope)}
                title="What the play button previews: the whole orientation, or only the selected stop"
                aria-label="Preview scope"
              >
                <option value="orientation">Orientation</option>
                <option value="stop" disabled={!selectedStepId}>
                  {selStep ? `Stop: ${selStep.name}` : 'Selected stop'}
                </option>
              </select>
              <GatedButton
                className={'oe-preview-play ' + (playing ? 'danger' : 'primary')}
                onClick={playing
                  ? stopPlay
                  : () => playOrientation(previewScope === 'stop' && selectedStepId ? [selectedStepId] : undefined)}
                reason={!hasAnyScript
                  ? 'This orientation has no authored beats yet'
                  : previewScope === 'stop' && !selectedStepId
                    ? 'Select a stop to preview it'
                    : previewScope === 'stop' && beats.length === 0
                      ? 'The selected stop has no authored beats yet'
                      : null}
                hint={playing
                  ? 'Stop the preview'
                  : previewScope === 'stop'
                    ? `Play "${selStep?.name ?? ''}" in 3D (camera + narration)`
                    : 'Play the whole orientation in 3D (camera + narration captions)'}
                aria-label={playing ? 'Stop preview' : 'Play preview'}
              >
                <Icon of={playing ? Icons.action.stop : Icons.action.play} size={14} />
              </GatedButton>
              <select
                value={playSpeed}
                onChange={(e) => setPlaySpeed(Number(e.target.value))}
                title="Playback speed — scales beat pacing and camera motion together (live while playing)"
                aria-label="Playback speed"
              >
                {[0.5, 1, 1.5, 2, 3, 5].map((s) => (
                  <option key={s} value={s}>{`${s}×`}</option>
                ))}
              </select>
            </div>
          )}
          <ViewToggle viewMode={viewMode} onChange={changeViewMode} />
          <button
            className={'oe-twins-toggle' + (showTwinMarkers ? ' on' : '')}
            aria-pressed={showTwinMarkers}
            title={showTwinMarkers ? 'Hide twin markers' : 'Show twin markers'}
            onClick={() => setShowTwinMarkers((v) => !v)}
          >
            <Icon of={Icons.content.twin} />
          </button>
          {viewMode === '3d' && hasSite && viewerLevel >= 33 && (
            <span className="oe-measure-slot" ref={setMeasureSlot} />
          )}
        </div>

        {viewMode === '3d' && hasSite && viewerLevel >= 33 && (
          <MeasureTool
            viewer={viewerRef}
            last={measureMsg}
            otherToolArmed={placingStop || pickTarget !== null}
            onArm={() => { setPlacingStop(false); setPickTarget(null); setMeasureMsg(null); }}
            buttonHost={measureSlot}
            viewerLevel={viewerLevel}
          />
        )}

        {/* Narration caption — shown ONLY when the current beat has narration.
            A no-narration beat shows nothing (no command summary stands in for it). */}
        {(playing || captionPreviewing) && playCaption && playCaption.text && (
          <div className="oe-play-caption" role="status" aria-live="polite">
            <div className="oe-play-meta">
              <span className="oe-play-stop">{playCaption.stop}</span>
              <span className="oe-play-count">{playCaption.progress}</span>
              <button className="oe-btn ghost sm" onClick={stopPlay}><Icon of={Icons.action.stop} size={12} /> Stop</button>
            </div>
            <div className="oe-play-text">{playCaption.text}</div>
          </div>
        )}

        {/* Comprehension-check quiz (Play preview): graded in the browser, no DB
            writes. The play loop gates on QuizPanel's onDone; results at the end. */}
        {activeCheck && <QuizPanel check={activeCheck} onDone={resolveActiveCheck} sceneSelection={selected3dTwin} twinNames={twinNames} />}

        {/* Interactive stop gate: fly into the tube marker, then Start/Advance. */}
        {stopGate && (
          <div className="oe-stop-gate" role="status" aria-live="polite">
            <div className="oe-stop-gate-title">{stepOf(stopGate.stepId)?.name || 'Stop'}</div>
            <div className="oe-stop-gate-hint">
              {stopGate.reached
                ? (stopGate.label === 'Start' ? "You're at the marker — start the orientation." : "You're at the marker — continue.")
                : 'Fly into the glowing marker to continue.'}
            </div>
            <div className="oe-stop-gate-actions">
              <button className="oe-btn ghost" onClick={flyToStopMarker}>Fly to marker</button>
              <button className="oe-btn" disabled={!stopGate.reached} onClick={confirmStopGate}>{stopGate.label}</button>
            </div>
          </div>
        )}
        {quizResults && (
          <QuizResults
            outcomes={quizResults}
            passingScorePct={currentOrientation?.passingScorePct ?? null}
            showAnswersAtEnd={currentOrientation?.showAnswersAtEnd ?? false}
            onClose={() => setQuizResults(null)}
          />
        )}

        {(placingStop || pickTarget) && (
          <div className="oe-place-hint">
            <span className="dot" />
            {placingStop
              ? (viewMode === '3d'
                ? 'Click a spot in the 3D view for the new stop (captures its elevation)'
                : 'Select a location on the map for the new stop')
              : (viewMode === '3d'
                ? 'Click a spot in the 3D view to set the location (captures elevation)'
                : 'Click the map to set the location')}
            <button className="oe-btn ghost sm" onClick={() => { setPlacingStop(false); setPickTarget(null); }}>Cancel</button>
          </div>
        )}

        {(placingStop || pickTarget) && viewMode === '3d' && hasSite && (
          <button
            className="oe-place-camera"
            onClick={placeAtCamera}
            title="Use the 3D camera's current position and elevation as the location"
          >
            Place at camera location
          </button>
        )}

        {stopsShell && (
          <StopsPanel
            stops={stopRows}
            selectedStepId={selectedStepId}
            onSelect={selectStop}
            onReorder={reorder}
            onReorderTo={orientationId ? reorderTo : undefined}
            onAddStop={orientationId ? toggleAddStop : undefined}
            onAddExisting={orientationId ? () => setModal('pick') : undefined}
            onCopy={orientationId ? (id) => void copyStop(id, (model.usage.get(id) ?? 0) > 1) : undefined}
            onRemove={orientationId ? removeStop : undefined}
            placing={placingStop}
            shell={stopsShell}
          />
        )}

        {inspectorShell && (
          <Inspector
            step={selStep}
            legacyVersions={legacyVersions}
            selectedAction={selectedAction}
            selectedCheck={selectedCheck}
            onCheckChange={onCheckChange}
            onDeleteCheck={onDeleteCheck}
            actionKey={selActionKey ?? selCheckKey}
            twinOptions={twinOptions}
            twinNames={twinNames}
            geoPropsByTwin={geoPropsByTwin}
            propsByTwin={propsByTwin}
            telemetryNamesByTwin={telemetryNamesByTwin}
            lineStringsByTwin={lineStringsByTwin}
            dynamicTwinIds={dynamicTwinIds}
            usedIn={selectedUsage}
            onStepPatch={onStepPatch}
            onActionChange={onActionChange}
            onDeleteAction={onDeleteAction}
            onCopyStop={selectedStepId ? () => void copyStop(selectedStepId, selectedUsage > 1) : undefined}
            onRemoveStop={() => removeStop()}
            onPickLocation={(t) => { setPlacingStop(false); setPickTarget((cur) => (cur === t ? null : t)); }}
            onPickPathNode={viewMode === '3d' ? ((i) => { setPlacingStop(false); setActivePathIndex(i); setPickTarget((cur) => (cur === 'pathNode' && activePathIndex === i ? null : 'pathNode')); }) : undefined}
            onPlacePathNodeAtCamera={viewMode === '3d' ? placePathNodeAtCamera : undefined}
            activePathIndex={pickTarget === 'pathNode' ? activePathIndex : null}
            onCaptureView={viewMode === '3d' ? captureFlyLookView : undefined}
            pickTarget={pickTarget}
            selected3dTwin={viewMode === '3d' ? selected3dTwin : null}
            onPreviewCommand={viewMode === '3d' ? previewCommand : undefined}
            onPreviewNarration={viewMode === '3d' ? previewNarration : undefined}
            onPreviewCheck={viewMode === '3d' ? previewCheckIn3d : undefined}
            onStopPreview={stopPreview}
            previewActive={previewingKey !== null && previewingKey === selActionKey}
            checkPreviewActive={previewingKey !== null && previewingKey === selCheckKey}
            dirty={dirty}
            shell={inspectorShell}
          />
        )}

        {drawerShell && (
          <Timeline
            step={selStep}
            beats={beats}
            playingBeat={playing && playPos && selStep && playPos.stepId === selStep.stepId ? playPos.bi : null}
            selAction={selAction}
            onSelectAction={(sel) => {
              setSelAction(sel);
              // Clicking a block must actually show its editor.
              if (sel) { setSelCheck(null); expandPanel('inspector'); }
            }}
            checks={selStep?.checks ?? []}
            selCheck={selCheck}
            onSelectCheck={(i) => {
              setSelCheck(i);
              if (i != null) { setSelAction(null); expandPanel('inspector'); }
            }}
            onAddCheck={onAddCheck}
            onAddBeat={onAddBeat}
            onDeleteBeat={onDeleteBeat}
            onAddAction={onAddAction}
            onMoveAction={onMoveAction}
            onMoveBeat={onMoveBeat}
            shell={drawerShell}
            twinNames={twinNames}
          />
        )}
      </div>
      )}

      {modal === 'new' && (
        <OrientationDetails
          title="New orientation"
          showStatus={false}
          checkCount={0}
          initial={{
            displayName: '', occupationTarget: '', description: '', autoAssign: false, status: 'Draft',
            // Module defaults for a fresh orientation (tables.rs column defaults).
            passingScorePct: null, showAnswersAtEnd: true, autoReassignOnFail: false, maxAttempts: null,
          }}
          onCancel={() => setModal(null)}
          onSave={createOrientation}
        />
      )}
      {modal === 'details' && currentOrientation && (
        <OrientationDetails
          title="Orientation details"
          showStatus
          checkCount={orientationCheckCount}
          createdBy={currentOrientation.createdBy ? authorName(currentOrientation.createdBy) : ''}
          initial={{
            displayName: currentOrientation.displayName,
            occupationTarget: currentOrientation.occupationTarget.join(', '),
            description: currentOrientation.description,
            autoAssign: currentOrientation.autoAssign,
            status: currentOrientation.status,
            passingScorePct: currentOrientation.passingScorePct,
            showAnswersAtEnd: currentOrientation.showAnswersAtEnd,
            autoReassignOnFail: currentOrientation.autoReassignOnFail,
            maxAttempts: currentOrientation.maxAttempts,
          }}
          onCancel={() => setModal(null)}
          onSave={saveDetails}
        />
      )}
      {modal === 'pick' && (
        <StepPicker
          candidates={pickerCandidates}
          onPick={addExistingStop}
          onCopy={(id) => void copyStop(id, false)}
          onDelete={deleteOrphanStep}
          onCancel={() => setModal(null)}
        />
      )}
      {modal === 'remove' && selStep && (
        <RemoveStopDialog
          stopName={selStep.name}
          onDelete={confirmDeleteStop}
          onKeep={confirmKeepStop}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}

function ViewToggle({ viewMode, onChange }: { viewMode: '2d' | '3d'; onChange: (m: '2d' | '3d') => void }) {
  return (
    <div className="oe-view-switch" role="tablist" aria-label="Map view">
      <button
        role="tab"
        aria-selected={viewMode === '2d'}
        className={viewMode === '2d' ? 'on' : ''}
        title="Author on the 2D map: place and drag stops, see geo overlays"
        onClick={() => onChange('2d')}
      >
        <Icon of={Icons.view.map} />
        <span className="label">2D Map</span>
      </button>
      <button
        role="tab"
        aria-selected={viewMode === '3d'}
        className={viewMode === '3d' ? 'on' : ''}
        title="Author in the 3D site view: pick locations with real elevation, select twins, preview camera moves and play the orientation"
        onClick={() => onChange('3d')}
      >
        <Icon of={Icons.view.viewer3d} />
        <span className="label">3D View</span>
      </button>
    </div>
  );
}
